<template>
  <va-form
    :id="isEdit ? page.id : ''"
    ref="form"
    v-model="form"
    :item="page"
    :disable-redirect="isEdit"
    :before-save="beforeSave"
    :after-save="handleAfterSave"
    :modify-before-send="prepareFormBeforeSave"
    :custom-redirect-route="customRedirectRoute"
    :success-redirect-link="successRedirectLink"
    :required-fields-set="requiredFieldsSet"
    @finishedSaving="finishedSaving">
    <v-row>
      <!--  Delete alternative content Modal --->
      <Modal
        width="400"
        :value="displayDeleteAlternativeContentModal"
        :title="$t('resources.pages.page_alternative_content_delete_modal_title')"
        :subtitle="$t('resources.pages.page_alternative_content_delete_modal_subtitle')"
        :cta-text="$t('generic.buttons.delete')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        @confirm="confirmAlternativeContentDelete"
        @cancel="cancelAlternativeContentDelete">
      </Modal>
      <!--  Delete alternative content Modal END--->
      <!-- Replace content modal -->
      <replace-content-modal
        :value="displayReplaceContentModal"
        :targets="replaceTargets"
        :can-undo="!!replaceSnapshot"
        @input="$event ? null : closeReplaceContentModal()"
        @replace-all="replaceAllContent"
        @undo="undoReplaceContent" />
      <!-- Replace content modal END -->
      <!-- Delete dynamic content modal -->
      <Modal
        width="400"
        :value="displayDeleteDynamicContentModal"
        :title="$t('resources.pages.page_dynamic_content_delete_modal_title')"
        :subtitle="$t('resources.pages.page_dynamic_content_delete_modal_subtitle')"
        :cta-text="$t('generic.buttons.delete')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        @confirm="confirmDynamicContentDelete"
        @cancel="cancelDynamicContentDelete">
      </Modal>
      <!-- Delete all additional sections modal -->
      <Modal
        width="400"
        :value="displayDeleteAllSectionsModal"
        :title="$t('resources.pages.additional_sections_delete_all_modal_title')"
        :subtitle="$t('resources.pages.additional_sections_delete_all_modal_subtitle')"
        :cta-text="$t('generic.buttons.delete')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        @confirm="confirmDeleteAllSections"
        @cancel="cancelDeleteAllSections">
      </Modal>
      <!-- Delete label START -->
      <Modal
        width="400"
        :value="displayDeleteLabelModal"
        :title="$t('resources.labels.label_delete_modal_title')"
        :subtitle="$t('resources.labels.label_delete_modal_subtitle')"
        :cta-text="$t('generic.buttons.delete')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        @confirm="confirmDeleteLabel"
        @cancel="cancelDeleteLabel">
      </Modal>
      <!-- Delete label END -->

      <!-- Phone number save notice modal -->
      <Modal
        width="400"
        :value="displayActionPhoneNumberModal === 'save'"
        :title="$t('resources.pages.phone_number_save_notice_title')"
        :subtitle="$t('resources.pages.phone_number_save_notice_description')"
        :cta-text="$t('generic.buttons.ok')"
        :cta-color="'primary'"
        :dialog-icon="'info'"
        @confirm="confirmPhoneNumberSaveNotice"
        @cancel="cancelPhoneNumberAction">
      </Modal>
      <!-- Phone number save notice modal END -->

      <!-- Delete phone number modal -->
      <Modal
        width="400"
        :value="displayActionPhoneNumberModal === 'delete'"
        :title="$t('resources.pages.delete_phone_number')"
        :subtitle="$t('resources.pages.delete_phone_number_description')"
        :cta-text="$t('generic.buttons.delete')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        @confirm="confirmDeletePhoneNumber"
        @cancel="cancelPhoneNumberAction">
      </Modal>
      <!-- Delete phone number modal END -->
      <!-- Impossible delete phone number modal -->
      <Modal
        width="400"
        :value="displayActionPhoneNumberModal === 'impossible_delete'"
        :title="$t('resources.pages.impossible_delete_phone_number')"
        :subtitle="$t('resources.pages.impossible_delete_phone_number_description')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        :hide-actions="true"
        @cancel="cancelPhoneNumberAction">
      </Modal>
      <!-- Impossible delete phone number modal END -->
      <!-- Need to replace phone number modal -->
      <Modal
        width="400"
        :value="displayActionPhoneNumberModal === 'need_to_replace'"
        :title="$t('resources.pages.phone_number_need_to_replace')"
        :subtitle="$t('resources.pages.phone_number_need_to_replace_description')"
        :cta-text="$t('generic.buttons.replace_number')"
        :cta-color="'primary'"
        :dialog-icon="'error'"
        @confirm="displayActionPhoneNumberModal = 'replace'"
        @cancel="cancelPhoneNumberAction">
      </Modal>
      <!-- Need to replace phone number modal END -->
      <!-- Missing phone numbers modal -->
      <Modal
        width="400"
        :value="displayActionPhoneNumberModal === 'missing_phone_numbers'"
        :title="$t('resources.pages.missing_phone_numbers_modal_title')"
        :subtitle="$t('resources.pages.missing_phone_numbers_modal_subtitle')"
        :cta-text="$t('resources.pages.edit_phone_numbers')"
        :cta-color="'primary'"
        :dialog-icon="'error'"
        @confirm="handleEditPhoneNumbers"
        @cancel="cancelPhoneNumberAction">
      </Modal>
      <!-- Missing phone numbers modal END -->
      <Modal
        width="400"
        v-if="phoneNumberActionData"
        :value="displayActionPhoneNumberModal === 'replace'"
        :title="$t('resources.pages.phone_number_replace')"
        :subtitle="
          $t('resources.pages.phone_number_replace_description', {
            phoneNumber: `${phoneNumberActionData.phoneNumberData.campaignName} / ${phoneNumberActionData.phoneNumberData.publisherName}`,
          })
        "
        :cta-text="$t('generic.buttons.replace_number')"
        :cta-color="'primary'"
        :dialog-icon="'error'"
        @confirm="confirmPhoneNumberReplace"
        @cancel="cancelPhoneNumberAction">
        <div class="spacing-top--24">
          <v-select
            :menu-props="{ offsetY: true, offsetOverflow: true }"
            outlined
            v-model="replacePhoneNumberVariable"
            filled
            return-object
            label="Ringba phone number"
            :items="filteredPhoneNumbers">
            <template v-slot:append>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7.41 8.58008L12 13.1701L16.59 8.58008L18 10.0001L12 16.0001L6 10.0001L7.41 8.58008Z"
                  fill="#939099" />
              </svg>
            </template>
          </v-select>
        </div>
      </Modal>

      <!-- Delete Ringba tag START -->
      <Modal
        width="400"
        :value="displayDeleteRingbaTagModal"
        :title="$t('resources.ringba_tags.ringba_tag_delete_modal_title')"
        :cta-text="$t('generic.buttons.delete')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        @confirm="confirmDeleteRingbaTag"
        @cancel="cancelDeleteRingbaTag">
      </Modal>
      <!-- Delete Ringba tag END -->
      <!--- Page version change Modal -->
      <Modal
        width="448"
        :value="displayVersionHistoryModal"
        :title="$t('resources.pages.page_history_modal_title')"
        :cta-text="$t('resources.pages.page_history_modal_cta_text')"
        :cta-disabled="!selectedVersion"
        :cta-loading="changePageVersionLoading"
        @confirm="confirmVersionChange"
        @cancel="cancelVersionChange"
        actions-border>
        <v-select
          class="spacing-top--20"
          v-model="selectedVersion"
          :items="versionsOptions"
          :label="$t('resources.pages.page_version_select_label')"
          :placeholder="$t('resources.pages.page_version_select_placeholder')"
          persistent-placeholder
          :menu-props="{ offsetY: true, offsetOverflow: true }"
          @change="handleVersionChange"
          hide-details="auto">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
          <template v-slot:item="{ item }">
            <div class="v-list-item__content">
              <div class="v-list-item__title">{{ item.text }}</div>
            </div>
            <div v-if="item.disabled" class="ml-auto">
              <v-chip class="in-use-chip" :color="'subtitle'" outlined :text-color="'subtitle'">
                {{ $t("resources.pages.version_in_use") }}
              </v-chip>
            </div>
          </template>
        </v-select>
        <span v-if="!selectedVersion" class="preview_selected_version">{{
          $t("resources.pages.preview_selected_version_disclaimer")
        }}</span>
        <div v-else class="preview-version-label" @click="previewPageVersion">
          <v-icon size="16">mdi-dock-window</v-icon> {{ $t("resources.pages.preview_this_version") }}
        </div>
      </Modal>
      <!--- Page version change Modal END -->
      <v-col cols="8">
        <!-- Content -->
        <v-card class="spacing-bottom--24">
          <v-card-title>{{ $t("resources.pages.page_content_block_title") }} </v-card-title>
          <div class="pre-header-input spacing-bottom--16" v-if="ability.can('view', 'pre-headline field')">
            <span class="label">{{ $t("resources.pages.pre_header") }}</span>
            <div class="pre-header-wrapper">
              <div class="input-wrapper">
                <span> {{ $t("resources.pages.pre_header_subtitle") }} </span>
                <v-switch
                  inset
                  color="primary"
                  :hide-details="'auto'"
                  :ripple="false"
                  v-model="form.showPreHeader"
                  @change="animatePreHeader"></v-switch>
              </div>
              <va-tinymce-input
                ref="preHeaderTextField"
                class="pre-header-input"
                :class="form.showPreHeader ? 'visible' : 'hidden'"
                source="preHeader"
                :current-content="form.preHeader"
                :editor-height="350"
                :stripped-down="true"
                :editor-data="{
                  variables: this.phoneNumberVariables,
                }"
                :imageUploadQueue.sync="preHeaderImageUploadQueue"
                @update="updatePreHeader" />
            </div>
          </div>

          <div class="d-flex align-center spacing-bottom--16">
            <va-text-input
              class="spacing-right--16"
              source="headline"
              v-model="form.headline"
              required
              :label="$t('resources.pages.headline')"
              :placeholder="$t('resources.pages.headline_placeholder')"></va-text-input>
            <v-checkbox
              source="showHeadline"
              v-model="form.showHeadline"
              :label="$t('resources.pages.page_headline_hide')"
              inset
              color="primary"
              class="spacing-top--19"
              :hide-details="'auto'"
              :ripple="false"
              :true-value="false"
              :false-value="true"></v-checkbox>
          </div>

          <va-tinymce-input
            source="mainContent"
            :title="$t('resources.pages.page_main_content')"
            :current-content="form.mainContent"
            :editor-height="739"
            :key="versionKey"
            :display-errors="submitAttempted"
            :imageUploadQueue.sync="imageUploadQueue"
            required
            @update="updateMainContent" />

          <!-- Additional sections block -->
          <div class="additional-sections-block">
            <div v-if="!additionalSectionsVisible" class="d-flex align-center spacing-top--16">
              <div>
                <div class="additional-sections-block__title">
                  {{ $t("resources.pages.additional_sections_title") }}
                </div>
                <div class="additional-sections-block__subtitle">
                  {{ $t("resources.pages.additional_sections_subtitle") }}
                </div>
              </div>
              <v-btn
                @click="additionalSectionsVisible = true"
                class="additional-sections-block__add-btn left-icon ml-auto"
                color="secondary-btn"
                plain>
                <plus-icon class="fill--primary mr-2"></plus-icon>
                {{ $t("resources.pages.additional_sections_add") }}
              </v-btn>
            </div>
            <v-expand-transition>
              <additional-sections-panel
                v-if="additionalSectionsVisible"
                ref="additionalSectionsPanel"
                v-model="form.additionalSections"
                :rtb-sorting="isRtbSortingOn"
                :image-upload-queue.sync="imageUploadQueue"
                @content-change="recountPhoneNumbers"
                @replace="openReplaceContentModal"
                @delete-all="displayDeleteAllSectionsModal = true"
                @close="additionalSectionsVisible = false" />
            </v-expand-transition>
          </div>
          <!-- Additional sections block END -->

          <div v-if="ability.can('view', 'server-side cloaking')" class="server-side-cloaking-wrapper">
            <div>
              <span>{{ $t("resources.pages.enable_server_side_cloaking") }}</span>
              <v-switch
                v-model="form.serverSideContentSplit"
                inset
                color="primary"
                :hide-details="'auto'"
                :ripple="false"></v-switch>
            </div>
            <div class="error-wrapper" v-for="error in formErrors.serverSideContentSplit" :key="error">
              {{ error }}
            </div>
          </div>

          <!--- Alternative content block -->
          <div class="alternative-content" v-if="ability.can('view', 'alternative content')">
            <div class="d-flex items-center spacing-top--16 align-center">
              <div>
                <div class="alternative-content__title">
                  {{ $t("resources.pages.page_alternative_content_block_title") }}
                </div>
                <div class="alternative-content__subtitle">
                  {{ $t("resources.pages.page_alternative_content_block_subtitle") }}
                </div>
              </div>
              <template v-if="!alternativeContentVisible">
                <v-btn
                  @click="alternativeContentVisible = !alternativeContentVisible"
                  class="alternative-content__add-btn left-icon ml-auto"
                  color="secondary-btn"
                  plain>
                  <plus-icon class="fill--primary mr-2"></plus-icon>
                  {{ $t("resources.pages.page_alternative_content_block_add") }}
                </v-btn>
              </template>
              <template v-else>
                <v-btn
                  @click="displayDeleteAlternativeContentModal = true"
                  class="alternative-content__delete-btn left-icon ml-auto"
                  color="error"
                  plain>
                  <v-icon small>mdi-trash-can-outline</v-icon>
                  {{ $t("resources.pages.page_alternative_content_block_delete") }}
                </v-btn>
              </template>
            </div>
            <v-expand-transition>
              <div v-if="alternativeContentVisible" class="alternative-content__inner">
                <div>
                  <va-autocomplete-input
                    ref="alternativeContentAutocomplete"
                    required
                    class="create-alternative-content-select"
                    clearable
                    :value="form.sharedAlternativeContent"
                    source="sharedAlternativeContent"
                    reference="alternative_contents"
                    :label="$t('resources.pages.alternative_content_creation_method')"
                    :placeholder="$t('resources.pages.alternative_content_creation_method_placeholder')"
                    item-value="@id"
                    :alternativeDropdownView="true"
                    :min-chars="0"
                    :items-per-page="5"
                    :append-item="pageAlternativeContent"
                    showLoadMore
                    customItem
                    @change="onCreateAlternativeContentChange">
                    <template v-slot:item="{ item, on }">
                      <v-list-item v-on="on" v-show="!item.hidden">
                        <v-list-item-content>
                          <v-list-item-title class="alternative-content-item">
                            <div>{{ item.name }}</div>
                            <router-link :to="`/alternative-contents/${item.id}/edit`" target="_blank">
                              <v-icon color="primary" size="16">mdi-open-in-new</v-icon>
                              <span>View</span>
                            </router-link>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                    <template #append-item-outer>
                      <empty-select-search-list
                        v-if="alternativeContentEmpty()"
                        resource-name="alternative_contents"
                        :empty-title="$t('generic.no_results_found')"
                        @click="createResourceHandler">
                      </empty-select-search-list>
                      <v-list-item
                        class="create-alternative-content-item"
                        :input-value="form.sharedAlternativeContent === pageAlternativeContent['@id']"
                        @click="onCreateAlternativeContentChange(pageAlternativeContent['@id'])">
                        <v-list-item-action class="create-alternative-content-action">
                          <v-icon size="16" color="primary">mdi-plus</v-icon>
                          <span>
                            {{ $t("resources.pages.alternative_content_create") }}
                          </span>
                        </v-list-item-action>
                      </v-list-item>
                    </template>
                    <template #no-data>
                      <empty-select-search-list
                        resource-name="alternative_contents"
                        :empty-title="$t('generic.no_results_found')"
                        @click="createResourceHandler">
                      </empty-select-search-list>
                    </template>
                  </va-autocomplete-input>
                </div>
                <v-expand-transition>
                  <div
                    v-if="
                      form.sharedAlternativeContent && form.sharedAlternativeContent !== pageAlternativeContent['@id']
                    ">
                    <v-text-field
                      v-model="form.sharedAlternativeContentUrl"
                      required
                      persistent-placeholder
                      hide-details="auto"
                      :disabled="sharedContentHasUrl()"
                      :rules="[rules.url]"
                      :error="!!formErrors.sharedAlternativeContentUrl"
                      :error-messages="formErrors.sharedAlternativeContentUrl"
                      :label="$t('resources.pages.alternative_content_url')"
                      :placeholder="$t('resources.pages.alternative_content_url_placeholder')"></v-text-field>
                    <div class="alternative-no-url-disclaimer" v-if="sharedContentHasUrl()">
                      <v-icon size="24" color="info"> mdi-information </v-icon>
                      <span>
                        <span>{{ $t("resources.pages.alternative_content_no_url_disclaimer") }}</span>
                        <span @click="openGlobalAlternativeContent" class="alternative-no-url-disclaimer-link">{{
                          $t("resources.pages.alternative_content_disclaimer_url")
                        }}</span>
                        <span>.</span>
                      </span>
                    </div>
                  </div>
                </v-expand-transition>
                <v-expand-transition>
                  <div v-if="createPageAlternativeContent">
                    <div>
                      <div class="d-flex spacing-bottom--16">
                        <va-autocomplete-input
                          class="mr-2 prepend-icon-hidden"
                          :label="$t('resources.pages.page_alternative_content_countries_label')"
                          v-model="alternativeContentConditions.selectedCountries"
                          :choices="countriesData"
                          :source="'loneAlternativeContent.conditionals.selectedCountries'"
                          cache-items
                          :placeholder="$t('resources.pages.page_alternative_content_countries_placeholder')"
                          multiple></va-autocomplete-input>

                        <va-autocomplete-input
                          class="ml-2 prepend-icon-hidden"
                          :label="$t('resources.pages.page_alternative_content_states_label')"
                          v-model="alternativeContentConditions.selectedStates"
                          :choices="statesData"
                          :source="'loneAlternativeContent.conditionals.selectedStates'"
                          cache-items
                          :placeholder="$t('resources.pages.page_alternative_content_states_placeholder')"
                          multiple></va-autocomplete-input>
                      </div>
                      <div class="d-flex spacing-bottom--16">
                        <va-autocomplete-input
                          class="mr-2 prepend-icon-hidden"
                          :label="$t('resources.pages.page_alternative_content_cities_label')"
                          v-model="alternativeContentConditions.selectedCities"
                          :choices="citiesData"
                          :source="'loneAlternativeContent.conditionals.selectedCities'"
                          cache-items
                          :placeholder="$t('resources.pages.page_alternative_content_cities_placeholder')"
                          multiple
                          hideSelectAll></va-autocomplete-input>

                        <va-autocomplete-input
                          class="ml-2 prepend-icon-hidden"
                          :label="$t('resources.pages.page_alternative_content_counties_label')"
                          v-model="alternativeContentConditions.selectedCounties"
                          :choices="countiesData"
                          :source="'loneAlternativeContent.conditionals.selectedCounties'"
                          cache-items
                          :placeholder="$t('resources.pages.page_alternative_content_counties_placeholder')"
                          multiple></va-autocomplete-input>
                      </div>
                      <div class="d-flex spacing-bottom--16">
                        <va-select-input
                          :label="$t('resources.pages.page_alternative_content_devices_label')"
                          class="device__select prepend-icon-hidden"
                          multiple
                          v-model="alternativeContentConditions.selectedDevices"
                          :choices="devices"
                          :source="'loneAlternativeContent.conditionals.selectedDevices'"
                          cache-items
                          :placeholder="$t('resources.pages.page_alternative_content_devices_placeholder')"
                          :menu-props="{ offsetY: true, offsetOverflow: true }"></va-select-input>
                      </div>
                    </div>
                    <v-text-field
                      class="spacing-bottom--16"
                      v-model="form.loneAlternativeContent.headline"
                      required
                      persistent-placeholder
                      hide-details
                      :label="$t('resources.pages.page_alternative_content_headline')"
                      :placeholder="$t('resources.pages.headline_placeholder')"></v-text-field>
                    <div class="d-flex py-3">
                      <v-switch
                        class="enable-cloaking-parameter-switch"
                        :label="$t('resources.pages.page_alternative_content_cloaking_parameter_label')"
                        v-model="alternativeContentConditions.enableCloakingParameter"
                        color="primary"
                        :ripple="false"
                        hide-details
                        inset></v-switch>
                    </div>

                    <div class="d-flex py-3">
                      <v-switch
                        class="enable-utm-parameters-switch"
                        :label="$t('resources.pages.page_alternative_content_utm_parameters_label')"
                        v-model="alternativeContentConditions.enableUTMParameters"
                        @change="enableUTMParametersChange"
                        color="primary"
                        :ripple="false"
                        hide-details
                        inset></v-switch>
                      <info-tooltip
                        class="enable-utm-parameters-switch-tooltip"
                        :info="$t('resources.pages.page_alternative_content_utm_parameters_tooltip')"></info-tooltip>
                    </div>

                    <!-- UTM params -->
                    <v-expand-transition>
                      <div v-if="alternativeContentConditions.enableUTMParameters" class="expandable-content">
                        <div>
                          <div class="page-alternative-content-click-parameters-title spacing-top--16">
                            {{ $t("resources.pages.page_alternative_content_click_parameters") }}
                          </div>
                          <div>
                            <v-checkbox
                              v-model="alternativeContentConditions.checkUniqueClickId"
                              :label="$t('resources.pages.page_alternative_content_check_unique')" />
                          </div>
                        </div>
                        <div class="d-flex spacing-bottom--16">
                          <va-text-input
                            class="domain-details__card--url flex-1 mr-2"
                            v-model.trim="alternativeContentConditions.utmSource"
                            :prefix="'UTM_SOURCE='"
                            :label="'Source'"
                            :placeholder="'SOURCE'"></va-text-input>
                          <va-text-input
                            class="domain-details__card--url flex-1 ml-2"
                            v-model.trim="alternativeContentConditions.utmMedium"
                            :prefix="'UTM_MEDIUM='"
                            :label="'Medium'"
                            :placeholder="'MEDIUM'"></va-text-input>
                        </div>

                        <div class="d-flex spacing-bottom--16">
                          <va-text-input
                            class="domain-details__card--url flex-1 mr-2"
                            v-model.trim="alternativeContentConditions.utmCampaign"
                            :prefix="'UTM_CAMPAIGN='"
                            :label="'Campaign'"
                            :placeholder="'CAMPAIGN'"></va-text-input>
                          <va-text-input
                            class="domain-details__card--url flex-1 ml-2"
                            v-model.trim="alternativeContentConditions.utmTerm"
                            :prefix="'UTM_TERM='"
                            :label="'Term'"
                            :placeholder="'TERM'"></va-text-input>
                        </div>

                        <div class="d-flex">
                          <va-text-input
                            class="domain-details__card--url pr-2 max-w-50"
                            v-model.trim="alternativeContentConditions.utmContent"
                            :prefix="'UTM_CONTENT='"
                            :label="'Content'"
                            :placeholder="'CONTENT'"></va-text-input>
                        </div>
                      </div>
                    </v-expand-transition>

                    <va-tinymce-input
                      class="spacing-bottom--16 spacing-top--16"
                      :title="$t('resources.pages.page_alternative_content')"
                      :current-content="alternativeContent"
                      :editor-height="739"
                      :imageUploadQueue.sync="alternativeContentImageUploadQueue"
                      :key="versionKey"
                      @update="updateAlternativeContent" />
                    <VaJsonInput
                      :label="$t('resources.pages.page_alternative_content_json_label')"
                      v-model="alternativeContentConditionsJson"></VaJsonInput>
                  </div>
                </v-expand-transition>
              </div>
            </v-expand-transition>
            <!--- Alternative content block END-->
          </div>

          <!-- Dynamic content block -->
          <div class="dynamic-content" v-if="ability.can('view', 'calendars')">
            <div class="d-flex items-center spacing-top--16 align-center">
              <div>
                <div class="dynamic-content__title">
                  {{ $t("resources.pages.page_dynamic_content_block_title") }}
                </div>
                <div class="dynamic-content__subtitle">
                  {{ $t("resources.pages.page_dynamic_content_block_subtitle") }}
                </div>
              </div>
              <template v-if="!dynamicContentVisible">
                <v-btn
                  @click="dynamicContentVisible = !dynamicContentVisible"
                  class="dynamic-content__add-btn left-icon ml-auto"
                  color="secondary-btn"
                  plain>
                  <plus-icon class="fill--primary mr-2"></plus-icon>
                  {{ $t("resources.pages.page_dynamic_content_block_add") }}
                </v-btn>
              </template>
              <template v-else>
                <v-btn
                  @click="displayDeleteDynamicContentModal = true"
                  class="dynamic-content__delete-btn left-icon ml-auto"
                  color="error"
                  plain>
                  <v-icon small>mdi-trash-can-outline</v-icon>
                  {{ $t("resources.pages.page_dynamic_content_block_delete") }}
                </v-btn>
              </template>
            </div>
            <v-expand-transition>
              <div v-if="dynamicContentVisible">
                <va-autocomplete-input
                  class="spacing-top--16"
                  v-model="form.calendar"
                  ref="calendarsAutocomplete"
                  :items-per-page="5"
                  :min-chars="0"
                  :label="$t('resources.pages.page_calendar_label')"
                  :placeholder="$t('resources.pages.page_calendar_placeholder')"
                  source="calendar"
                  reference="calendars"
                  item-text="name"
                  item-value="@id"
                  :alternativeDropdownView="true"
                  showLoadMore>
                  <template v-slot:[`no-data`]>
                    <empty-select-search-list
                      resource-name="calendars"
                      :empty-title="$t('resources.calendars.no_calendars_available')"
                      @click="createResourceHandler">
                    </empty-select-search-list>
                  </template>
                </va-autocomplete-input>
                <v-expand-transition>
                  <div v-if="form.calendar">
                    <v-text-field
                      class="spacing-top--16"
                      hide-details
                      persistent-placeholder
                      v-model="form.calendarContent.headline"
                      :label="$t('resources.pages.page_calendar_headline_label')"
                      :placeholder="$t('resources.pages.page_calendar_headline_placeholder')"></v-text-field>
                    <va-tinymce-input
                      class="spacing-top--16"
                      :title="$t('resources.pages.page_calendar_content_label')"
                      :current-content="dynamicContent"
                      :editor-height="739"
                      :imageUploadQueue.sync="dynamicContentImageUploadQueue"
                      :key="versionKey"
                      @update="updateDynamicContent" />
                  </div>
                </v-expand-transition>
              </div>
            </v-expand-transition>
          </div>
        </v-card>

        <!-- Images -->
        <va-expandable-wrapper
          class="spacing-bottom--24"
          :title="$t('resources.pages.page_images_block_title')"
          :open="imagesBlockVisible">
          <VaImageDragAndDropInput
            :item-id="form.featuredImage"
            :label="$t('resources.pages.page_featured_image')"
            @update:photo="updateFeaturedImage"></VaImageDragAndDropInput>
          <p class="page-subcard-disclaimer spacing-bottom--16">
            {{ $t("resources.pages.page_featured_image_disclaimer") }}
          </p>
          <va-text-input
            class="spacing-bottom--16"
            v-model="form.featuredImageCaption"
            :label="$t('resources.pages.featured_caption')"
            :placeholder="$t('resources.pages.featured_caption_placeholder')"
            source="featuredImageCaption"></va-text-input>

          <VaImageDragAndDropInput
            v-if="form.type !== PAGE_TYPE.LANDER"
            :item-id="form.thumbnailImage"
            :label="$t('resources.pages.page_thumbnail_image')"
            @update:photo="updateThumbnailImage"></VaImageDragAndDropInput>
          <p v-if="form.type !== PAGE_TYPE.LANDER" class="page-subcard-disclaimer spacing-bottom--16">
            {{ $t("resources.pages.page_thumbnail_image_disclaimer") }}
          </p>
        </va-expandable-wrapper>

        <!-- Excerpt -->
        <va-expandable-wrapper
          v-if="form.type !== PAGE_TYPE.LANDER"
          class="spacing-bottom--24"
          :title="$t('resources.pages.page_excerpt_block_title')"
          :open="false">
          <va-tinymce-input
            source="excerpt"
            :title="$t('resources.pages.excerpt_text')"
            :current-content="form.excerpt"
            :editor-height="275"
            :info="$t('resources.pages.excerpt_tooltip_text')"
            :stripped-down="true"
            :imageUploadQueue.sync="excerptImageUploadQueue"
            @update="updateExcerptText" />
        </va-expandable-wrapper>

        <!-- Code -->
        <va-expandable-wrapper :title="$t('resources.pages.page_code_block_title')" :open="codeBlockVisible">
          <code-input
            v-model="form.headCustomCode"
            class="spacing-bottom--16"
            :info="$t('resources.pages.page_head_section_code_tooltip_text')"
            :title="$t('resources.pages.page_head_section_code')"></code-input>

          <code-input
            v-model="form.bodyCustomCode"
            :info="$t('resources.pages.page_body_section_code_tooltip_text')"
            :title="$t('resources.pages.page_body_section_code')"></code-input>
        </va-expandable-wrapper>
      </v-col>

      <v-col cols="4">
        <!-- Details -->
        <v-card class="spacing-bottom--24">
          <v-card-title class="card-title details-title">
            <div>
              <v-icon class="card-icon">mdi-web</v-icon>
              {{ $t("resources.pages.page_details_block_title") }}
            </div>
            <v-tooltip top v-if="statusObject.tooltipText">
              <template v-slot:activator="{ on, attrs }">
                <v-chip :color="statusObject.chipColor" class="status-chip" v-bind="attrs" v-on="on">
                  {{ statusObject.text }}
                </v-chip>
              </template>
              {{ statusObject.tooltipText }}
            </v-tooltip>
            <v-chip v-else :color="statusObject.chipColor" class="status-chip chip-outlined">
              {{ statusObject.text }}
            </v-chip>
          </v-card-title>

          <va-domain-autocomplete
            :disabled="isDomainProvided || isEdit"
            class="spacing-bottom--16"
            required
            source="domain"
            v-model="form.domain"
            :initial-domain="domainSeed"
            :label="$t('resources.pages.page_domain')"
            :placeholder="$t('resources.pages.page_domain_placeholder')"
            @change="domainChanged"
            @item-selected="selectedDomain = $event"></va-domain-autocomplete>

          <va-slug-checker
            v-model="form.slug"
            :domain="form.domain"
            :label="$t('resources.pages.page_slug')"
            :no-check="isEdit"
            :source="'slug'"
            :edit-form="isEdit"
            @updateLoading="val => (this.isLoading = val)">
          </va-slug-checker>
          <div v-if="isEdit && fullUrl" class="url-wrapper spacing-bottom--16">
            <a :href="fullUrl" target="_blank">
              {{ fullUrl }}
            </a>
            <CopyText display-icon :value="fullUrl"></CopyText>
          </div>

          <va-select-input
            v-if="ability.can('view', 'page type')"
            class="spacing-bottom--16 spacing-top--16"
            :source="'type'"
            v-model="form.type"
            :label="$t('resources.pages.page_type')"
            :choices="PAGE_TYPES"
            @change="pageTypeChanged">
          </va-select-input>
          <va-autocomplete-input
            class="spacing-bottom--16"
            required
            v-if="isArticlePage"
            :label="$t('resources.pages.page_category')"
            :placeholder="$t('resources.pages.page_category_placeholder')"
            v-model="form.category"
            source="category"
            reference="page_categories"
            :filter="resourcesFilter"
            ref="pageCategorySelect"
            item-value="@id"
            :min-chars="0"
            :items-per-page="5"
            showLoadMore>
          </va-autocomplete-input>

          <div v-if="isArticlePage" class="d-flex align-center spacing-bottom--16">
            <span class="page-setting-label d-flex align-center">
              {{ $t("resources.pages.use_homepage_theme") }}
              <info-tooltip
                class="use-homepage-theme-tooltip ml-1"
                :info="$t('resources.pages.use_homepage_theme_tooltip')"></info-tooltip>
            </span>
            <v-switch
              v-model="form.useHomepageTheme"
              class="ml-auto page-setting-switch"
              inset
              color="primary"
              :hide-details="'auto'"
              :ripple="false"></v-switch>
          </div>

          <div class="d-flex" :class="{ 'spacing-top--16': !isEdit, 'spacing-bottom--16': isEdit }">
            <span class="page-setting-label">{{ $t("resources.pages.pass_query_parameters") }}</span>
            <v-switch
              v-model="form.passQueryParameters"
              class="ml-auto page-setting-switch"
              inset
              color="primary"
              :hide-details="'auto'"
              :ripple="false"></v-switch>
          </div>

          <div v-if="ability.can('view', 'ringba parameters')" class="d-flex spacing-bottom--16 spacing-top--16">
            <span class="page-setting-label">{{ $t("resources.pages.add_ringba_parameters") }}</span>
            <v-switch
              class="ml-auto page-setting-switch"
              v-model="showRingbaParameters"
              @change="handleRingbaSwitch"
              inset
              color="primary"
              :hide-details="'auto'"
              :ripple="false">
            </v-switch>
          </div>

          <v-expand-transition v-if="ability.can('view', 'ringba parameters')">
            <div class="ringba-block" v-if="showRingbaParameters">
              <va-array-input
                v-model="form.ringbaParameters"
                v-slot="props"
                :column-layout-without-drag="true"
                source="ringbaParameters"
                :label="false"
                :block-label="$t('resources.pages.ringba_block.block_title')"
                :hide-details="'auto'"
                :add-button-text="$t('resources.pages.ringba_block.button_title')">
                <va-text-input
                  v-model="form.ringbaParameters[props.index].tag"
                  class="spacing-vertical--6"
                  :label="$t('resources.pages.ringba_block.tag_label')"
                  :placeholder="$t('resources.pages.ringba_block.tag_placeholder')"
                  required
                  v-bind="props"
                  source="tag"></va-text-input>
                <v-text-field
                  :label="$t('resources.pages.ringba_block.number_label')"
                  class="spacing-vertical--6"
                  name="number"
                  :rules="[rules.required]"
                  :placeholder="$t('resources.pages.ringba_block.number_placeholder')"
                  v-model.lazy="form.ringbaParameters[props.index].number"
                  validate-on-blur
                  persistent-placeholder
                  hide-details="auto"
                  type="tel"
                  v-cleave="{
                    delimiters: ['+'],
                    blocks: [0, 11],
                    numericOnly: true,
                    value: form.ringbaParameters[props.index].number,
                  }"
                  required>
                  <template v-slot:message="{ message }">
                    {{ message }}
                  </template>
                </v-text-field>

                <div class="ringba-phone-input-caption spacing-vertical--8">
                  {{ $t("resources.pages.ringba_block.ringba-phone-caption") }}
                </div>
              </va-array-input>
            </div>
          </v-expand-transition>

          <div class="d-flex spacing-top--16" :class="{ 'spacing-bottom--16': isEdit && !hasAdditionalSections }">
            <span class="page-setting-label">{{ $t("resources.pages.enable_lazy_loading") }}</span>
            <v-switch
              class="ml-auto page-setting-switch"
              v-model="form.lazyLoad"
              inset
              color="primary"
              :hide-details="'auto'"
              :ripple="false">
            </v-switch>
          </div>

          <div v-if="hasAdditionalSections" :class="{ 'spacing-bottom--16': isEdit }">
            <div class="d-flex spacing-top--16">
              <span class="page-setting-label">{{ $t("resources.pages.rtb_sorting") }}</span>
              <v-switch
                class="ml-auto page-setting-switch"
                :input-value="isRtbSortingOn"
                :disabled="!isRtbSortingAvailable"
                inset
                color="primary"
                :hide-details="'auto'"
                :ripple="false"
                @change="form.rtbSorting = $event">
              </v-switch>
            </div>
            <div v-if="isClientSideContentSplit" class="page-setting-caption">
              {{ $t("resources.pages.rtb_sorting_client_side_split_hint") }}
            </div>
            <div v-else-if="hasInlineContentSplit" class="page-setting-caption">
              {{ $t("resources.pages.rtb_sorting_inline_content_split_hint") }}
            </div>
            <div v-else-if="!isRtbSortingAvailable" class="page-setting-caption">
              {{ $t("resources.pages.rtb_sorting_no_enabled_sections_hint") }}
            </div>
          </div>

          <!---Page Version -->
          <div v-if="isEdit" class="page-version-block">
            <div class="page-version-block__label">
              {{ $t("resources.pages.last_saved") }}
            </div>
            <div class="d-flex">
              <div class="page-version-block__info">{{ lastSavedPageVersionLabel }}</div>
              <div @click="handleDisplayVersionHistoryModal" class="page-version-block__view-history ml-auto">
                {{ $t("resources.pages.view_history") }}
              </div>
            </div>
          </div>

          <!---Page Version -->
          <div class="version-description-block">
            <div class="d-flex">
              <v-textarea
                :label="$t('resources.pages.version_description')"
                :placeholder="$t('resources.pages.version_description_placeholder')"
                rows="3"
                persistent-placeholder
                v-model="form.versionDescription"></v-textarea>
            </div>
          </div>

          <!-- Upload History -->
          <div v-if="isEdit" class="upload-history-block">
            <div class="d-flex align-center">
              <div class="upload-history-block__label">
                <span class="upload-history-block__title">
                  {{ $t("resources.pages.upload_status") }}
                </span>
                <v-chip
                  v-if="currentUploadedStatus && currentUploadedStatus.simplifiedStatus"
                  :color="currentUploadedStatus.backgroundColor"
                  class="upload-history-block__status-chip"
                  :style="{ color: currentUploadedStatus.color }">
                  {{ currentUploadedStatus.simplifiedStatus }}
                </v-chip>
              </div>
              <div @click="loadStatusHistory" class="upload-history-block__view-link ml-auto">
                <refresh-icon></refresh-icon><span class="ml-1">{{ $t("resources.pages.refresh") }}</span>
              </div>
            </div>

            <div class="upload-history-block__content">
              <div v-for="(status, index) in statusHistory" :key="status.id">
                <div v-if="index === 0">
                  <div class="d-flex align-center justify-space-between mt-2">
                    <div class="upload-history-block__first-line text-truncate">
                      {{
                        $t("resources.pages.current_version_label", {
                          date: status.createdAt,
                          name: status.user,
                        })
                      }}
                    </div>
                    <div
                      v-show="!showUploadedStatus"
                      @click="showUploadedStatus = true"
                      class="upload-history-block__view-link">
                      {{ $t("resources.pages.view_history") }}
                    </div>
                  </div>
                  <v-expand-transition>
                    <div v-show="showUploadedStatus">
                      <div class="upload-history-block__status">
                        {{ $t("resources.pages.actions") }}: {{ status.actions }}
                      </div>
                      <div>{{ $t("resources.pages.status") }}: {{ status.simplifiedStatus }}</div>
                    </div>
                  </v-expand-transition>
                </div>
                <v-expand-transition v-else>
                  <div v-show="showUploadedStatus" class="mt-4">
                    <div class="upload-history-block__first-line text-truncate">
                      {{
                        $t("resources.pages.current_version_label", {
                          date: status.createdAt,
                          name: status.user,
                        })
                      }}
                    </div>
                    <div class="upload-history-block__status">
                      {{ $t("resources.pages.actions") }}: {{ status.actions }}
                    </div>
                    <div>{{ $t("resources.pages.status") }}: {{ status.simplifiedStatus }}</div>
                  </div>
                </v-expand-transition>
              </div>
              <div class="d-flex justify-end mt-2" v-if="showUploadedStatus">
                <div @click="showUploadedStatus = false" class="upload-history-block__view-link">
                  {{ $t("resources.pages.show_less") }}
                </div>
              </div>
            </div>
          </div>
          <!---Page Version END -->
          <div v-if="ability.can('view', 'page ab test status') && form.displayAbTest" class="spacing-top--16">
            <div class="ab-test-label">
              {{ $t("resources.pages.ab_test") }}
              <div>
                <v-chip
                  v-if="form.displayAbTest.status === AB_TEST_STATUSES.ACTIVE"
                  class="status-chip active-chip"
                  color="info-120">
                  {{ $t("resources.ab_tests.active") }}
                </v-chip>
                <v-chip class="status-chip" v-if="form.displayAbTest.status === AB_TEST_STATUSES.DRAFT">
                  {{ $t("resources.ab_tests.draft") }}
                </v-chip>
                <v-chip
                  class="status-chip inactive-chip"
                  v-if="form.displayAbTest.status === AB_TEST_STATUSES.INACTIVE">
                  {{ $t("resources.ab_tests.inactive") }}
                </v-chip>
              </div>
            </div>
            <div class="ab-test-name" @click="openAbTest">
              {{ form.displayAbTest.name }}
            </div>
          </div>
        </v-card>

        <!-- Page Elements -->
        <v-card class="spacing-bottom--24">
          <v-card-title class="card-title">
            <v-icon class="card-icon">mdi-shape-square-plus</v-icon>
            {{ $t("resources.pages.page_elements_block_title") }}
          </v-card-title>
          <v-card-subtitle v-if="!form.domain" class="page-elements-card-subtitle">
            {{ $t("resources.pages.page_elements_block_subtitle") }}
          </v-card-subtitle>
          <va-multiple-expandable-input
            class="page-elements-panel-wrapper"
            v-if="form.domain"
            :items="pageElementItems">
            <!-- Header -->
            <template v-slot:[`header-action`]>
              <v-switch
                v-model="form.hasHeader"
                @change="showHeaderChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`header-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.header"
                ref="headersAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_header')"
                :placeholder="$t('resources.pages.page_header_placeholder')"
                source="header"
                reference="headers"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="headers"
                    :empty-title="$t('resources.headers.no_headers_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_header_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>
            <!-- Alternative Header -->
            <template v-slot:[`alternative_header-action`]>
              <v-switch
                v-model="alternativeHeaderEnabled"
                @change="showAlternativeHeaderChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`alternative_header-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.alternativeHeader"
                ref="alternativeHeadersAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_alternative_header')"
                :placeholder="$t('resources.pages.page_alternative_header_placeholder')"
                source="alternativeHeader"
                reference="headers"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="headers"
                    :empty-title="$t('resources.headers.no_headers_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_alternative_header_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- Theme -->
            <template v-slot:[`theme-action`]>
              <v-switch
                v-model="hasTheme"
                @change="showThemeChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`theme-content`]>
              <va-select-input
                source="template"
                v-model="form.template"
                :label="$t('resources.pages.page_theme')"
                :placeholder="$t('resources.pages.page_theme_placeholder')"
                :choices="themeTemplateChoices">
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_theme_tooltip_text')"></info-tooltip>
                </template>
              </va-select-input>
            </template>

            <!-- Alternative Theme -->
            <template v-slot:[`alternative_theme-action`]>
              <v-switch
                v-model="hasAlternativeTheme"
                @change="showAlternativeThemeChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`alternative_theme-content`]>
              <va-select-input
                source="alternativeTemplate"
                v-model="form.alternativeTemplate"
                :label="$t('resources.pages.page_alternative_theme')"
                :placeholder="$t('resources.pages.page_alternative_theme_placeholder')"
                :choices="themeTemplateChoices">
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_alternative_theme_tooltip_text')"></info-tooltip>
                </template>
              </va-select-input>
            </template>

            <!-- Live Chat Header -->
            <template v-slot:[`livechat_header-action`]>
              <v-switch
                v-model="form.liveChatHeader"
                @change="showLivechatHeaderChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`livechat_header-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.liveChatHeaderTemplate"
                :label="$t('resources.pages.page_livechat_header')"
                :placeholder="$t('resources.pages.page_livechat_header_placeholder')"
                source="liveChatHeaderTemplate"
                :choices="LIVE_CHAT_HEADER_TEMPLATES"
                item-value="value"
                cache-items>
              </va-autocomplete-input>

              <va-text-input
                class="mt-4"
                source="liveChatHeaderName"
                :label="$t('resources.pages.page_livechat_header_name')"
                v-model="form.liveChatHeaderName" />

              <VaImageDragAndDropInput
                class="mt-4"
                :label="$t('resources.pages.page_livechat_header_image')"
                :item-id="form.liveChatHeaderImage"
                source="liveChatHeaderImage"
                @update:photo="updateLiveChatHeaderImage"></VaImageDragAndDropInput>
            </template>

            <!-- Footer -->
            <template v-slot:[`footer-action`]>
              <v-switch
                v-model="form.hasFooter"
                @change="showFooterChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`footer-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.footer"
                ref="footersAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_footer')"
                :placeholder="$t('resources.pages.page_footer_placeholder')"
                source="footer"
                reference="footers"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="footers"
                    :empty-title="$t('resources.footers.no_footers_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_footer_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- Alternative Footer -->
            <template v-slot:[`alternative_footer-action`]>
              <v-switch
                v-model="alternativeFooterEnabled"
                @change="showAlternativeFooterChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`alternative_footer-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.alternativeFooter"
                ref="alternativeFootersAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_alternative_footer')"
                :placeholder="$t('resources.pages.page_alternative_footer_placeholder')"
                reference="footers"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="footers"
                    :empty-title="$t('resources.footers.no_footers_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_alternative_footer_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <template v-slot:[`author-content`]>
              <va-autocomplete-input
                clearable
                v-model="form.author"
                source="author"
                reference="authors"
                :label="$t('resources.pages.author')"
                :placeholder="$t('resources.pages.author_placeholder')"
                item-value="@id"
                :alternativeDropdownView="true"
                :min-chars="0"
                :items-per-page="5"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="authors"
                    :empty-title="$t('generic.no_results_found')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.author_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- Pop-up -->
            <template v-slot:[`popup-action`]>
              <v-switch
                v-model="form.showExitPopups"
                @change="showPopupChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`popup-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.modal"
                ref="modalsAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_popup')"
                :placeholder="$t('resources.pages.page_popup_placeholder')"
                source="modal"
                reference="modals"
                item-text="name"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="popups"
                    :empty-title="$t('resources.popups.no_popups_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_popup_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- Sidebar -->
            <template v-slot:[`sidebar-action`]>
              <v-switch
                v-model="form.hasSidebar"
                @change="showSidebarChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`sidebar-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.sidebar"
                ref="sidebarsAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_sidebar')"
                :placeholder="$t('resources.pages.page_sidebar_placeholder')"
                source="sidebar"
                reference="sidebars"
                item-text="name"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="sidebars"
                    :empty-title="$t('resources.sidebars.no_sidebars_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_sidebar_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- Favorites-block -->
            <template v-slot:[`favorites_block-action`]>
              <v-switch
                v-model="form.showFavoritesBlock"
                @change="showFavoritesBlockChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`favorites_block-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.favoritesBlock"
                ref="favoritesAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_favorites_block')"
                :placeholder="$t('resources.pages.page_favorites_block_placeholder')"
                source="favoritesBlock"
                reference="favorites_blocks"
                item-text="name"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="favorites_blocks"
                    :empty-title="$t('resources.favorites_blocks.no_favorites_blocks_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_favorites_block_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- SiteRunner Branding -->
            <template v-slot:[`siterunner_branding-action`]>
              <v-switch v-model="form.showBranding" color="primary" :ripple="false" hide-details inset></v-switch>
            </template>
            <template v-slot:[`siterunner_branding-content`]>
              <v-text-field
                hide-details
                persistent-placeholder
                v-model="form.brandingLink"
                :label="$t('resources.pages.page_branding_link_label')"
                :placeholder="$t('resources.pages.page_branding_link_placeholder')"></v-text-field>
            </template>
            <!-- Page Background -->
            <template v-slot:[`page_background-action`]>
              <v-switch
                v-model="form.customBackground"
                color="primary"
                :ripple="false"
                hide-details
                inset
                @change="hasBackgroundChange"></v-switch>
            </template>
            <template v-slot:[`page_background-content`]>
              <v-btn-toggle
                class="w-full va-btn-toggle"
                v-model="pageBackgroundType"
                @change="backgroundTypeChange"
                mandatory
                tile
                color="primary"
                group>
                <v-btn class="flex-1" :value="'color'">
                  {{ $t("resources.pages.page_background_btn_color") }}
                </v-btn>

                <v-btn class="flex-1" :value="'image'">
                  {{ $t("resources.pages.page_background_btn_image") }}
                </v-btn>
              </v-btn-toggle>

              <div class="pt-4">
                <template v-if="pageBackgroundType === 'color'">
                  <va-color-picker
                    :key="bgColorPickerKey"
                    source="customBackgroundColor"
                    :title="$t('resources.pages.page_background_color')"
                    v-model="form.customBackgroundColor" />
                </template>
                <template v-if="pageBackgroundType === 'image'">
                  <VaImageDragAndDropInput
                    :label="$t('resources.pages.page_background_image')"
                    :item-id="form.customBackgroundImage"
                    source="customBackgroundImage"
                    @update:photo="updateBackgroundImage"></VaImageDragAndDropInput>
                </template>
                <va-text-input
                  prefix="%"
                  class="mt-4"
                  source="customBackgroundHeight"
                  :label="$t('resources.pages.page_background_length')"
                  v-model="form.customBackgroundHeight"
                  :rules="[rules.backgroundHeight]" />
                <va-select-input
                  v-if="pageBackgroundType === 'image'"
                  class="spacing-bottom--16 spacing-top--16"
                  v-model="form.customBackgroundProperty"
                  :label="$t('resources.pages.page_background_property')"
                  :choices="BACKGROUND_PROPERTIES">
                </va-select-input>

                <div class="spacing-top--16 spacing-bottom--16">
                  <div class="page-setting-label spacing-bottom--8">
                    {{ $t("resources.pages.page_background_starting_point") }}
                  </div>
                  <v-radio-group v-model="form.customBackgroundStartingPoint" hide-details row>
                    <v-radio
                      :label="$t('resources.pages.page_background_starting_point_top')"
                      value="top"
                      color="primary">
                    </v-radio>
                    <v-radio
                      :label="$t('resources.pages.page_background_starting_point_bottom')"
                      value="bottom"
                      color="primary">
                    </v-radio>
                  </v-radio-group>
                </div>
                <div class="required-text"><span>*</span> {{ $t("resources.pages.page_background_disclaimer") }}</div>
              </div>
            </template>
          </va-multiple-expandable-input>
        </v-card>
        <!-- Labels -->
        <v-card class="spacing-bottom--24">
          <v-card-title class="card-title label-card-title-wrapper">
            <div>
              <v-icon class="card-icon">mdi-label-multiple-outline</v-icon>
              {{ $t("resources.pages.labels") }}
            </div>
            <div v-if="!isLabelCreateActive" class="label-add-button" @click="isLabelCreateActive = true">
              <plus-icon class="fill--primary mr-1"></plus-icon>
              {{ $t("resources.pages.create_label") }}
            </div>
          </v-card-title>
          <v-card-subtitle
            v-if="labels.length === 0 && !isLabelCreateActive && !labelListLoading"
            class="labels-empty-subtitle">
            {{ $t("resources.pages.labels_empty") }}
          </v-card-subtitle>
          <div v-if="isLabelCreateActive">
            <v-form ref="labelForm" @submit.prevent="saveLabel" class="label-form">
              <v-text-field
                class="label-name-input"
                :label="$t('resources.pages.label_name')"
                :placeholder="$t('resources.pages.label_name_placeholder')"
                persistent-placeholder
                v-model="newLabel.name"
                :rules="[
                  v => !!v || $t('resources.pages.label_name_required'),
                  v => (v && v.length < 11) || $t('resources.pages.label_name_too_long'),
                ]" />
              <va-color-picker :title="$t('resources.pages.label_color')" v-model="newLabel.color" />
              <div class="label-create-button-wrapper">
                <v-btn @click="isLabelCreateActive = false" class="label-cancel-button" outlined>
                  {{ $t("resources.pages.cancel") }}
                </v-btn>
                <v-btn type="submit" color="primary">{{ $t("resources.pages.create") }}</v-btn>
              </div>
            </v-form>
          </div>
          <div v-else-if="labels.length > 0 || labelListLoading">
            <v-autocomplete
              class="label-primary-select"
              :label="$t('resources.pages.label_primary')"
              :placeholder="$t('resources.pages.label_primary_placeholder')"
              persistent-placeholder
              item-text="name"
              item-value="@id"
              append-icon="mdi-chevron-down"
              :items="labels"
              clearable
              v-model="form.label">
              <template #item="{ item }">
                <v-row class="label-select-item">
                  <v-avatar class="label-badge ml-4 mr-2" size="24" :color="item.color"></v-avatar>
                  <span class="label-title">{{ item.name }}</span>
                  <v-icon
                    v-show="false"
                    class="label-select-item__close-icon"
                    :key="item.name"
                    @click.prevent.stop="deleteLabel(item)"
                    size="16"
                    color="icon">
                    mdi-trash-can-outline
                  </v-icon>
                </v-row>
              </template>
              <template #selection="{ item }">
                <v-avatar class="label-badge mr-2" size="24" :color="item.color"></v-avatar> {{ item.name }}
              </template>
              <template #no-data>
                <div class="v-list-item theme--light">
                  <div class="v-list-item__content">
                    <div class="v-list-item__title">
                      {{ $t("resources.pages.no_labels_found") }}
                    </div>
                  </div>
                </div>
              </template>
            </v-autocomplete>
            <v-autocomplete
              :class="[
                'additional-label-autocomplete',
                additionalLabelSearch && additionalLabelSearch.length && 'v-autocomplete--search-added',
              ]"
              ref="additionalLabelAutoComplete"
              :label="$t('resources.pages.label_additional')"
              :placeholder="$t('resources.pages.label_additional_placeholder')"
              return-object
              item-text="name"
              :items="labels"
              :search-input.sync="additionalLabelSearch"
              append-icon="mdi-chevron-down"
              prepend-inner-icon="mdi-plus"
              multiple
              persistent-placeholder
              hide-details
              v-model="form.additionalLabels">
              <template #no-data>
                <div class="v-list-item theme--light">
                  <div class="v-list-item__content">
                    <div class="v-list-item__title">
                      {{ $t("resources.pages.no_labels_found") }}
                    </div>
                  </div>
                </div>
              </template>
              <template #selection> </template>
              <template #item="{ item }">
                <div
                  @mouseover="hoveredAdditionalLabel = item"
                  @mouseleave="hoveredAdditionalLabel = null"
                  class="additional-label-item-wrapper">
                  <div class="additional-label-item-inner-wrapper">
                    <div class="v-list-item__action">
                      <v-icon>{{
                        $refs["additionalLabelAutoComplete"].hasItem(item)
                          ? "mdi-checkbox-marked"
                          : "mdi-checkbox-blank-outline"
                      }}</v-icon>
                    </div>
                    <div class="v-list-item__content">
                      <div class="v-list-item__title">
                        <v-avatar class="label-badge mr-2" size="24" :color="item.color"></v-avatar>
                        {{ item.name }}
                      </div>
                    </div>
                  </div>
                  <v-icon
                    v-show="hoveredAdditionalLabel === item"
                    class="label-select-item__close-icon"
                    :key="item.name"
                    @click.prevent.stop="deleteLabel(item)"
                    size="16"
                    color="icon">
                    mdi-trash-can-outline
                  </v-icon>
                </div>
              </template>
            </v-autocomplete>
            <div
              :class="[
                'd-flex',
                'flex-wrap',
                'selection-chip__wrapper',
                form.additionalLabels.length ? 'selection-chip__wrapper--has-selection' : '',
              ]">
              <v-chip
                v-for="item in form.additionalLabels"
                :key="`${item.id}-key`"
                class="additional-label-chip"
                :color="getChipColor(item, $vuetify.theme.dark)"
                :style="{ color: getChipTextColor(item, $vuetify.theme.dark) }">
                <span class="pr-2">
                  {{ item.name }}
                </span>
                <v-icon small @click="$refs['additionalLabelAutoComplete'].selectItem(item)">mdi-close</v-icon>
              </v-chip>
            </div>
          </div>
        </v-card>
        <!-- Ringba Phone Numbers -->
        <v-card class="spacing-bottom--24">
          <v-card-title class="card-title phone-number-card-title-wrapper">
            <div>
              <v-icon class="card-icon">mdi-phone-log</v-icon>
              {{ $t("resources.pages.phone_numbers") }}
            </div>
            <div
              v-if="!isPhoneNumberCreateActive && form.domain && isHaveNotUsedPhoneNumber"
              class="label-add-button"
              @click="isPhoneNumberCreateActive = true">
              <plus-icon class="fill--primary mr-1"></plus-icon>
              {{ $t("resources.pages.add_phone_number") }}
            </div>
          </v-card-title>
          <v-card-subtitle v-if="!form.domain" class="phone-number-variable-empty-subtitle">
            {{ $t("resources.pages.select_domain_for_phone_numbers") }}
          </v-card-subtitle>
          <v-card-subtitle
            v-else-if="phoneNumberVariables.length === 0 && !isPhoneNumberCreateActive"
            class="phone-number-variable-empty-subtitle">
            {{ $t("resources.pages.no_phone_numbers") }}
          </v-card-subtitle>
          <div v-if="isPhoneNumberCreateActive" class="spacing-bottom--24">
            <ringba-phone-number
              :phone-numbers="phoneNumbers"
              :current-variables="phoneNumberVariables"
              @submit="addPhoneNumber">
              <template #default="{ isValid }">
                <div class="label-create-button-wrapper">
                  <v-btn @click="isPhoneNumberCreateActive = false" class="label-cancel-button" outlined>
                    {{ $t("resources.pages.cancel") }}
                  </v-btn>
                  <v-btn type="submit" color="primary" :disabled="!isValid">
                    {{ $t("resources.pages.save_changes") }}
                  </v-btn>
                </div>
              </template>
            </ringba-phone-number>
          </div>
          <div v-if="phoneNumberVariables.length > 0 && phoneNumbers.length > 0" class="phone-number-variable-wrapper">
            <v-expansion-panels v-model="phoneNumberBlockVisible" multiple>
              <div v-for="variable in phoneNumberVariables" :key="variable.name + variable.phoneNumber" class="w-full">
                <v-expansion-panel class="spacing-bottom--12 w-full expansion-panel" :value="variable['name']">
                  <v-expansion-panel-header class="expansion-panel-title">
                    <div class="phone-number-variable-header">
                      <span>
                        <span class="text-truncate flex-1">
                          {{ variable.text }}
                        </span>
                        <span
                          v-if="
                            !ringbaPhoneNumberCounts[variable.name] || variable.type === VARIABLE_TYPES.PHONE_NUMBER_OLD
                          ">
                          <v-chip
                            :color="'error'"
                            class="upload-history-block__status-chip"
                            :style="{ color: 'white' }">
                            {{
                              variable.type === VARIABLE_TYPES.PHONE_NUMBER_OLD
                                ? $t("resources.pages.missing_phone_number")
                                : $t("resources.pages.not_used_on_page")
                            }}
                          </v-chip>
                        </span>
                      </span>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="expansion-panel-content">
                    <div class="phone-number-variable-content">
                      <ringba-phone-number
                        @submit="handlePhoneNumberChange"
                        :current-variables="phoneNumberVariables"
                        :is-edit="true"
                        :value="variable"
                        :phone-numbers="phoneNumbers">
                        <template #default="{ isValid }">
                          <div
                            class="phone-number-variable-action-buttons"
                            :class="{
                              'phone-number-variable-action-buttons--border': isHaveNotUsedPhoneNumber,
                            }">
                            <v-btn @click="removePhoneNumber(variable)" color="error" plain :ripple="false">
                              <span class="phone-number-variable-delete-button">
                                <v-icon small class="mr-2">mdi-trash-can-outline</v-icon>
                                {{ $t("resources.pages.remove") }}
                              </span>
                            </v-btn>
                            <v-btn
                              v-if="isHaveNotUsedPhoneNumber"
                              type="submit"
                              color="primary"
                              :disabled="!isValid"
                              plain
                              :ripple="false">
                              <span class="phone-number-variable-save-button">
                                <refresh-icon :color="isValid ? '#6A49EF' : '#939099'"></refresh-icon>
                                <span class="ml-2">{{ $t("resources.pages.save_changes") }}</span>
                              </span>
                            </v-btn>
                          </div>
                        </template>
                      </ringba-phone-number>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </div>
            </v-expansion-panels>
          </div>
        </v-card>
        <!-- Ringba tags -->
        <v-card v-if="ability.can('view', 'ringba user tag')" class="spacing-bottom--24">
          <v-card-title class="card-title ringba-tag-card-title-wrapper">
            <div>
              <v-icon class="card-icon">mdi-tag-outline</v-icon>
              {{ $t("resources.pages.ringba_tag") }}
            </div>
            <div v-if="!isRingbaTagCreateActive" class="ringba-tag-add-button" @click="isRingbaTagCreateActive = true">
              <plus-icon class="fill--primary mr-1"></plus-icon>
              {{ $t("resources.pages.create_ringba_tag") }}
            </div>
          </v-card-title>
          <v-card-subtitle
            v-if="ringbaTags.length === 0 && !isRingbaTagCreateActive && !ringbaTagListLoading"
            class="ringba-tags-empty-subtitle">
            {{ $t("resources.pages.ringba_tag_empty") }}
          </v-card-subtitle>
          <div v-if="isRingbaTagCreateActive">
            <v-form ref="ringbaTagForm" @submit.prevent="saveRingbaTag" class="ringba-tag-form">
              <v-text-field
                class="ringba-tag-name-input"
                :label="$t('resources.pages.ringba_tag_name')"
                :placeholder="$t('resources.pages.ringba_tag_name_placeholder')"
                persistent-placeholder
                v-model="newRingbaTag.name"
                :rules="[
                  v => !!v || $t('resources.pages.ringba_tag_name_required'),
                  v => (v && v.length <= 255) || $t('resources.pages.ringba_tag_name_too_long'),
                ]" />
              <div class="ringba-tag-create-button-wrapper">
                <v-btn @click="isRingbaTagCreateActive = false" class="ringba-tag-cancel-button" outlined>
                  {{ $t("resources.pages.cancel") }}
                </v-btn>
                <v-btn :disabled="!newRingbaTag.name" type="submit" color="primary">{{
                  $t("resources.pages.create")
                }}</v-btn>
              </div>
            </v-form>
          </div>
          <div v-else-if="ringbaTags.length > 0 || ringbaTagListLoading">
            <v-autocomplete
              class="ringba-tag-primary-select"
              :label="$t('resources.pages.ringba_tag_primary')"
              :placeholder="$t('resources.pages.ringba_tag_primary_placeholder')"
              persistent-placeholder
              item-text="name"
              item-value="@id"
              append-icon="mdi-chevron-down"
              :items="ringbaTags"
              clearable
              v-model="form.ringbaTag">
              <template #no-data>
                <div class="v-list-item theme--light">
                  <div class="v-list-item__content">
                    <div class="v-list-item__title">
                      {{ $t("resources.pages.no_ringba_tag_found") }}
                    </div>
                  </div>
                </div>
              </template>
              <template #item="{ item }">
                <div
                  @mouseover="hoveredRingbaTag = item"
                  @mouseleave="hoveredRingbaTag = null"
                  class="ringba-tag-item-wrapper">
                  <div class="ringba-tag-item-inner-wrapper">
                    <div class="v-list-item__content">
                      <div class="v-list-item__title">
                        {{ item.name }}
                      </div>
                    </div>
                  </div>
                  <v-icon
                    v-show="hoveredRingbaTag === item"
                    class="ringba-tag-select-item__close-icon"
                    :key="item.name"
                    @click.prevent.stop="deleteRingbaTag(item)"
                    size="16"
                    color="icon">
                    mdi-trash-can-outline
                  </v-icon>
                </div>
              </template>
            </v-autocomplete>
          </div>
        </v-card>
        <!-- Sentry error logging -->
        <v-card v-if="ability.can('view', 'sentry error logging')" class="spacing-bottom--24">
          <v-card-title class="card-title sentry-card-title-wrapper">
            <div>
              <v-icon class="card-icon">mdi-alert-circle-outline</v-icon>
              {{ $t("resources.pages.sentry_error_logging") }}
            </div>
            <v-switch
              class="page-setting-switch"
              v-model="sentryEnabled"
              inset
              color="primary"
              :hide-details="'auto'"
              :ripple="false">
            </v-switch>
          </v-card-title>
          <v-card-subtitle class="sentry-card-subtitle">
            <div class="spacing-bottom--8">{{ $t("resources.pages.sentry_subtitle") }}</div>
            <div class="d-flex align-center">
              <span class="page-setting-caption">{{ sentryStateLabel }}</span>
              <span v-if="!isSentryInherited" @click="useDomainSentrySetting" class="page-setting-reset ml-auto">
                {{ $t("resources.pages.sentry_use_domain_setting") }}
              </span>
            </div>
          </v-card-subtitle>
        </v-card>
      </v-col>
    </v-row>

    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  import definedAbility from "@/ability";
  // Mixins
  import crudFormMixin from "@/mixins/crudFormMixin";
  import VaDomainAutocomplete from "@/components/custom/VaDomainAutocomplete";
  import providedDomain from "@/mixins/providedDomain";
  import Preview from "@/mixins/previewMethod";
  // Constants
  import {
    PAGE_STATUSES,
    PAGE_TYPES,
    PAGE_TYPE,
    AB_TEST_STATUSES,
    PAGE_UPLOAD_STATUSES,
    PAGE_HISTORY_STATUS,
    BACKGROUND_PROPERTIES,
    BLOGS_TEMPLATES,
    LIVE_CHAT_HEADER_TEMPLATES,
    VARIABLE_TYPES,
    SECTION_DEFAULT_TITLE,
  } from "@/constant";
  // Components
  import VaSlugChecker from "@/components/custom/VaSlugChecker";
  import VaExpandableWrapper from "@/components/custom/VaExpandableWrapper";
  import VaImageDragAndDropInput from "@/components/custom/VaImageDragAndDropInput";
  import VaMultipleExpandableInput from "@/components/custom/VaMultipleExpandableInput";
  import CodeInput from "@/components/custom/CodeInput";
  import InfoTooltip from "@/components/custom/InfoTooltip";
  import RequiredFieldText from "@/components/custom/RequiredFieldText";
  import EmptySelectSearchList from "@/components/custom/EmptySelectSearchList.vue";
  import Modal from "@/components/custom/Modal";
  import PlusIcon from "@/components/icons/PlusIcon";
  import RefreshIcon from "@/components/icons/RefreshIcon";
  import VaJsonInput from "@/components/custom/VaJsonInput";
  import CopyText from "@/components/custom/CopyText";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import RingbaPhoneNumber from "@/components/custom/RingbaPhoneNumber";
  import AdditionalSectionsPanel from "@/components/custom/AdditionalSectionsPanel";
  import ReplaceContentModal from "@/components/custom/ReplaceContentModal";
  // Utils
  import { parseToHtml } from "@/utility/parseToHtml";
  import { replaceInContent } from "@/utility/replaceContent";
  import { cleanupTinymceContent } from "@/utility/cleanupTinymceContent";
  import { serializeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
  import { convertAssetsPathToAbsoluteLinks, convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";
  import { setLazyLoad, removeLazyLoad } from "@/utility/setLazyLoad";
  import { getFullDateAndTime } from "@core/utils/filter";
  import { urlValidation } from "@/utility/urlValidation";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import { findInvalidTelHrefs } from "@/utility/validateTelHref";
  import { showTelConfirmModal } from "@/assets/utilities/tinymce/helpers/telConfirmModal";
  import { getChipColor, getChipTextColor } from "@/assets/utilities/helpers/getChipColor";
  // Data
  import statesData from "@/assets/data/states";
  import citiesData from "@/assets/data/cities";
  import countiesData from "@/assets/data/counties";
  import countriesData from "@/assets/data/countries";
  import devicesData from "@/assets/data/devices";
  // directives
  import cleave from "@/directives/cleave";
  import { v4 as uuidv4 } from "uuid";

  export default {
    name: "PageForm",
    components: {
      VaDomainAutocomplete,
      VaSlugChecker,
      VaExpandableWrapper,
      VaImageDragAndDropInput,
      VaMultipleExpandableInput,
      InfoTooltip,
      CodeInput,
      RequiredFieldText,
      EmptySelectSearchList,
      Modal,
      PlusIcon,
      VaJsonInput,
      CopyText,
      VaColorPicker,
      RefreshIcon,
      RingbaPhoneNumber,
      AdditionalSectionsPanel,
      ReplaceContentModal,
    },
    mixins: [crudFormMixin, providedDomain, Preview],
    directives: {
      cleave,
    },
    props: {
      // Determines if form is used while editing
      isEdit: {
        type: Boolean,
        default: false,
      },
      page: {
        type: Object,
        default: () => {},
      },
      needModal: {
        type: Boolean,
        default: false,
      },
      redirectRouteName: {
        type: String,
        default: "",
      },
      redirectRouteParams: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        selectedDomain: null,
        pageDomain: null,
        ability: definedAbility(),
        getChipColor,
        getChipTextColor,
        versionKey: null,
        displayReplaceContentModal: false,
        replaceSnapshot: null,
        PAGE_TYPES,
        PAGE_TYPE,
        PAGE_STATUSES,
        AB_TEST_STATUSES,
        BACKGROUND_PROPERTIES,
        BLOGS_TEMPLATES,
        LIVE_CHAT_HEADER_TEMPLATES,
        VARIABLE_TYPES,
        // Might have to set default values for ALL the fields here (!)
        form: {},
        // Page versioning
        versionsOptions: [],
        pageVersions: [],
        lastSavedPageVersionLabel: "",
        selectedVersion: null,
        displayVersionHistoryModal: false,
        changePageVersionLoading: false,
        imageUploadQueue: [],
        excerptImageUploadQueue: [],
        alternativeContentImageUploadQueue: [],
        dynamicContentImageUploadQueue: [],
        preHeaderImageUploadQueue: [],
        /**
         * When previewing a page, we always have to send the latest page slug, otherwise preview will fail.
         * - Let's say user restores some old version of a page where slug was different from the latest one,
         * preview would fail in that case;
         * Only used for previewing (!)
         */
        latestPageSlug: "",
        imagesBlockVisible: false,
        codeBlockVisible: false,
        hasAlternativeHeader: false,
        hasAlternativeFooter: false,
        hasTheme: false,
        hasAlternativeTheme: false,
        changedStatus: null,
        successRedirectLink: {},
        displayDeleteLabelModal: false,
        labelToDelete: {},
        dynamicContentVisible: false,
        displayDeleteDynamicContentModal: false,
        dynamicContent: "",
        additionalSectionsVisible: false,
        displayDeleteAllSectionsModal: false,
        // Alternative content properties
        displayDeleteAlternativeContentModal: false,
        alternativeContentVisible: false,
        alternativeContentConditions: {
          checkUniqueClickId: false,
          enableUTMParameters: false,
          enableCloakingParameter: false,
          utmSource: "",
          utmMedium: "",
          utmCampaign: "",
          utmTerm: "",
          utmContent: "",
          selectedStates: [],
          selectedCities: [],
          selectedCounties: [],
          selectedCountries: [],
          selectedDevices: [],
        },
        showUploadedStatus: false,
        statusHistory: [],
        labelStatusLoading: false,
        alternativeContent: "",
        statesData: statesData,
        citiesData: citiesData,
        countiesData: countiesData,
        showDeleteIcon: false,
        countriesData: countriesData,
        newLabel: {},
        labels: [],
        isLabelCreateActive: false,
        additionalLabelSearch: "",
        hoveredAdditionalLabel: null,
        labelListLoading: false,
        formErrors: {},
        devices: devicesData,
        showRingbaParameters: false,
        createPageAlternativeContent: false,
        pageAlternativeContent: {
          "@id": "page-alternative-content",
          name: this.$t("resources.pages.alternative_content_create"),
          hidden: true,
          skipBackendGet: true,
        },
        rules: {
          required: value => !!value || this.$t("generic.validation.number_to_replace"),
          url: value => urlValidation(value) || this.$t("generic.validation.url"),
          backgroundHeight: value => {
            const num = parseFloat(value);
            if (!this.form.customBackground) {
              return true;
            }
            if (isNaN(num) || value === "" || value === null) return this.$t("generic.validation.required");
            if (num < 0) return this.$t("resources.pages.page_background_height_must_be_greater_than_zero");
            if (num > 100) return this.$t("resources.pages.page_background_height_must_be_less_than_100");
            return true;
          },
        },
        ringbaTags: [],
        newRingbaTag: {},
        isRingbaTagCreateActive: false,
        hoveredRingbaTag: null,
        displayDeleteRingbaTagModal: false,
        ringbaTagListLoading: false,
        isPhoneNumberCreateActive: false,
        phoneNumbers: [],
        phoneNumberBlockVisible: [],
        ringbaNewPhoneNumber: {},
        displayActionPhoneNumberModal: null,
        phoneNumberActionData: null,
        ringbaPhoneNumberCounts: {},
        replacePhoneNumberVariable: null,
        pageBackgroundType: "color",
        bgColorPickerKey: 0,
      };
    },
    watch: {
      phoneNumberVariables: {
        handler(newValue) {
          window.tinymceVariables = newValue;
        },
      },
    },
    computed: {
      isHaveNotUsedPhoneNumber() {
        const usedPhoneNumbers = this.phoneNumberVariables
          .filter(variable => variable.phoneNumber)
          .map(variable => variable.phoneNumber);

        const unusedCount = this.phoneNumbers.filter(phone => !usedPhoneNumbers.includes(phone["@id"])).length;

        return unusedCount > 0;
      },
      filteredPhoneNumbers() {
        return this.phoneNumberVariables.filter(phoneNumber => phoneNumber.name !== this.phoneNumberActionData.name);
      },
      phoneNumberVariables() {
        const usedPhoneNumbers = { ...this.ringbaPhoneNumberCounts };

        const variables = this.form.variables
          ?.filter(variable => {
            return variable.type === VARIABLE_TYPES.PHONE_NUMBER || variable.type === VARIABLE_TYPES.PHONE_NUMBER_OLD;
          })
          .map(variable => {
            const phoneNumberData = this.phoneNumbers.find(phoneNumber => phoneNumber["@id"] === variable.phoneNumber);

            if (!phoneNumberData) {
              return {};
            }

            // Remove from usedPhoneNumbers if this variable exists
            if (usedPhoneNumbers[variable.name]) {
              delete usedPhoneNumbers[variable.name];
            }

            return {
              ...variable,
              phoneNumberData,
              value: variable.name,
              text: `${phoneNumberData.campaignName} / ${phoneNumberData.publisherName}`,
            };
          });

        if (!variables) {
          return [];
        }

        // Create missing phone numbers as placeholder to edit or replace them.
        const missingPhoneNumbers = Object.keys(usedPhoneNumbers)?.map(name => {
          return {
            text: usedPhoneNumbers[name],
            phoneNumberData: {},
            phoneNumber: "",
            value: name,
            name,
            type: VARIABLE_TYPES.PHONE_NUMBER_OLD,
          };
        });

        if (missingPhoneNumbers.length > 0) {
          variables.push(...missingPhoneNumbers);
        }

        return variables;
      },
      currentUploadedStatus() {
        const simplifiedStatus = this.statusHistory[0]?.simplifiedStatus;

        if (!simplifiedStatus) {
          return null;
        }

        if (simplifiedStatus === PAGE_UPLOAD_STATUSES.ERROR) {
          return {
            simplifiedStatus,
            backgroundColor: "rgba(255, 76, 81, 0.12)",
            color: "#FF7074",
          };
        }

        if (simplifiedStatus === PAGE_UPLOAD_STATUSES.SUCCESS) {
          return {
            simplifiedStatus,
            backgroundColor: "rgba(34, 185, 77, 0.12)",
            color: "#22B94D",
          };
        }

        return {
          simplifiedStatus,
          backgroundColor: "rgba(13, 110, 253, 0.12)",
          color: "#0D6EFD",
        };
      },
      alternativeContentConditionsJson: {
        get() {
          if (this.alternativeContentConditions.enableUTMParameters) {
            return this.alternativeContentConditions;
          } else {
            const data = {
              selectedStates: this.alternativeContentConditions.selectedStates,
              selectedCities: this.alternativeContentConditions.selectedCities,
              selectedCounties: this.alternativeContentConditions.selectedCounties,
              selectedCountries: this.alternativeContentConditions.selectedCountries,
              selectedDevices: this.alternativeContentConditions.selectedDevices,
            };

            if (this.alternativeContentConditions.enableCloakingParameter) {
              data.enableCloakingParameter = this.alternativeContentConditions.enableCloakingParameter;
            }

            return data;
          }
        },
        set(value) {
          if (this.alternativeContentConditions.enableUTMParameters) {
            this.alternativeContentConditions.utmSource = value.utmSource;
            this.alternativeContentConditions.utmMedium = value.utmMedium;
            this.alternativeContentConditions.utmCampaign = value.utmCampaign;
            this.alternativeContentConditions.utmTerm = value.utmTerm;
            this.alternativeContentConditions.utmContent = value.utmContent;
            this.alternativeContentConditions.checkUniqueClickId = value.checkUniqueClickId;
          }
          this.alternativeContentConditions.selectedStates = value.selectedStates || [];
          this.alternativeContentConditions.selectedCities = value.selectedCities || [];
          this.alternativeContentConditions.selectedCounties = value.selectedCounties || [];
          this.alternativeContentConditions.selectedCountries = value.selectedCountries || [];
          this.alternativeContentConditions.selectedDevices = value.selectedDevices || [];
          this.alternativeContentConditions.enableCloakingParameter = value.enableCloakingParameter || false;
        },
      },
      customRedirectRoute() {
        return this.isEdit
          ? null
          : {
              name: "pages_list",
            };
      },
      fullUrl() {
        const domainItem = this.selectedDomain;

        if (!domainItem || !this.form.slug) {
          return "";
        }

        if (this.form.slug === "/") {
          return "https://" + domainItem.text + "/";
        }

        return "https://" + domainItem.text + "/" + this.form.slug + "/";
      },
      /**
       * Used when filtering autocomplete inputs' results.
       * Only the results for current domain have to be displayed
       */
      resourcesFilter() {
        return this.form.domain ? { domain: this.form.domain.replace("/api/domains/", "") } : null;
      },
      domainSentryEnabled() {
        return this.selectedDomain?.sentryEnabled ?? false;
      },
      domainSeed() {
        return this.isDomainProvided ? this.selectedDomainInfo : this.pageDomain;
      },
      isSentryInherited() {
        return this.form.sentryEnabled === null;
      },
      sentryEnabled: {
        get() {
          return this.isSentryInherited ? this.domainSentryEnabled : this.form.sentryEnabled;
        },
        set(value) {
          this.form.sentryEnabled = value;
        },
      },
      sentryStateLabel() {
        return this.isSentryInherited
          ? this.$t("resources.pages.sentry_inherited", {
              state: this.domainSentryEnabled ? this.$t("generic.on") : this.$t("generic.off"),
            })
          : this.$t("resources.pages.sentry_overridden");
      },
      isArticlePage() {
        return this.form.type === PAGE_TYPE.ARTICLE;
      },
      themeTemplateChoices() {
        return this.BLOGS_TEMPLATES.map(item => ({
          text: item.label,
          value: item.value,
        }));
      },
      alternativeHeaderEnabled: {
        get() {
          return this.hasAlternativeHeader;
        },
        set(value) {
          this.hasAlternativeHeader = value;
          this.form.alternativeHeaderEnabled = value;
        },
      },
      alternativeFooterEnabled: {
        get() {
          return this.hasAlternativeFooter;
        },
        set(value) {
          this.hasAlternativeFooter = value;
          this.form.alternativeFooterEnabled = value;
        },
      },
      pageElementItems() {
        const items = [
          {
            title: "Header",
            slotName: "header",
            hidden: false,
            expanded: this.form.hasHeader,
          },
          {
            title: "Alternative Header",
            slotName: "alternative_header",
            hidden: false,
            expanded: this.hasAlternativeHeader,
          },
          {
            title: "Theme",
            slotName: "theme",
            hidden: false,
            expanded: this.hasTheme,
          },
          {
            title: "Alternative Theme",
            slotName: "alternative_theme",
            hidden: false,
            expanded: this.hasAlternativeTheme,
          },
          {
            title: "Livechat header",
            slotName: "livechat_header",
            hidden: false,
            expanded: this.form.liveChatHeader,
          },
          {
            title: "Footer",
            slotName: "footer",
            hidden: false,
            expanded: this.form.hasFooter,
          },
          {
            title: "Alternative Footer",
            slotName: "alternative_footer",
            hidden: false,
            expanded: this.hasAlternativeFooter,
          },
          {
            title: "Author",
            slotName: "author",
            hidden: false,
            expanded: true,
            actionHidden: true,
          },
          {
            title: "Pop-up",
            slotName: "popup",
            hidden: false,
            expanded: this.form.showExitPopups,
          },
          {
            title: "Sidebar",
            slotName: "sidebar",
            hidden: false,
            expanded: this.form.hasSidebar,
          },
          {
            title: "Favorites block",
            slotName: "favorites_block",
            hidden: false,
            expanded: this.form.showFavoritesBlock,
          },
          {
            title: "SiteRunner branding",
            slotName: "siterunner_branding",
            hidden: !this.ability.can("view", "siterunner branding"),
            expanded: this.form.showBranding,
          },
          {
            title: "Page background",
            slotName: "page_background",
            hidden: !this.ability.can("view", "page background options"),
            expanded: this.form.customBackground,
          },
        ];

        const filteredItems = items.filter(item => {
          // If page type is "Form"
          if (this.form.type === PAGE_TYPE.AUDIO_FORM) {
            return item.slotName !== "header";
          }

          if (this.isArticlePage) {
            return !["theme", "alternative_theme"].includes(item.slotName);
          }

          return item;
        });

        return filteredItems;
      },
      requiredFormFields() {
        let fields = ["headline", "mainContent", "domain", "slug"];
        if (this.isArticlePage) {
          fields.push("category");
        }

        return fields;
      },
      statusObject() {
        return PAGE_STATUSES.find(item => item.value === this.form.status);
      },
      hasAdditionalSections() {
        return (this.form.additionalSections?.length || 0) > 0;
      },
      isClientSideContentSplit() {
        return (this.alternativeContentVisible || this.dynamicContentVisible) && !this.form.serverSideContentSplit;
      },
      hasInlineContentSplit() {
        return (this.form.mainContent || "").includes("content-split-data");
      },
      isRtbSortingAvailable() {
        const hasEnabledSections = (this.form.additionalSections || []).some(section => section.enabled);
        return hasEnabledSections && !this.isClientSideContentSplit && !this.hasInlineContentSplit;
      },
      isRtbSortingOn() {
        return !!this.form.rtbSorting && this.isRtbSortingAvailable;
      },
      replaceTargets() {
        const targets = [
          {
            key: "main",
            label: this.$t("resources.pages.page_main_content"),
            content: this.form.mainContent || "",
          },
        ];

        (this.form.additionalSections || []).forEach(section => {
          targets.push({
            key: section._key,
            label: section.title || this.$t("resources.pages.additional_sections_title"),
            content: section.content || "",
          });
        });

        return targets;
      },
    },
    async beforeMount() {
      const isDuplicateForm = !this.isEdit && !!this.page;

      if (this.page?.versions?.length) {
        this.pageVersions = Object.assign([], this.page.versions);
        this.setVersionItems(this.page.versions, this.page.versions[0]);
        // Set latest page version label
        this.setLastSavedLabel(this.page.versions[0]);
      }

      // Transform some of the form values. (Comes back as object from back-end, but has to be transformed to IRI)
      this.pageDomain = typeof this.page?.domain === "object" ? this.page.domain : null;
      this.form = this.page || {};
      this.form.domain = this.page?.domain?.["@id"];
      this.form.modal = this.page?.modal?.["@id"];
      this.form.label = this.page?.label?.["@id"];
      this.form.ringbaTag = this.page?.ringbaTag?.["@id"];
      this.form.sidebar = this.page?.sidebar?.["@id"];
      this.form.header = this.page?.header?.["@id"];
      this.form.footer = this.page?.footer?.["@id"];
      this.form.favoritesBlock = this.page?.favoritesBlock?.["@id"];
      this.form.featuredImage = this.page?.featuredImage?.["@id"];
      this.form.featuredImageCaption = this.page?.featuredImageCaption || "";
      this.form.thumbnailImage = this.page?.thumbnailImage?.["@id"];
      this.form.category = this.page?.category?.["@id"];
      this.form.author = this.page?.author?.["@id"];
      this.form.hasHeader = this.page?.hasHeader || false;
      this.form.hasFooter = this.page?.hasFooter || false;
      this.form.alternativeHeader = this.page?.alternativeHeader?.["@id"];
      this.form.alternativeFooter = this.page?.alternativeFooter?.["@id"];
      this.form.alternativeHeaderEnabled = this.page?.alternativeHeaderEnabled || false;
      this.form.alternativeFooterEnabled = this.page?.alternativeFooterEnabled || false;
      this.hasAlternativeHeader = this.page?.alternativeHeaderEnabled || false;
      this.hasAlternativeFooter = this.page?.alternativeFooterEnabled || false;
      this.form.hasSidebar = this.page?.hasSidebar || false;
      this.form.showExitPopups = this.page?.showExitPopups || false;
      this.form.showFavoritesBlock = this.page?.showFavoritesBlock || false;
      this.form.useHomepageTheme = this.page?.useHomepageTheme || false;
      this.form.lazyLoad = this.page?.lazyLoad || false;
      this.$set(this.form, "rtbSorting", this.page?.rtbSorting || false);
      // When duplicating, Sentry logging is always off - it has to be switched on deliberately
      this.$set(this.form, "sentryEnabled", isDuplicateForm ? false : this.page?.sentryEnabled ?? null);
      this.form.additionalLabels = this.page?.additionalLabels || [];
      this.form.ringbaParameters = this.page?.ringbaParameters || [];
      this.showRingbaParameters = this.page?.ringbaParameters.length || false;
      this.form.versionDescription = this.page?.versionDescription || "";

      this.form.variables = this.page?.variables?.map(variable => {
        return {
          ...variable,
          phoneNumber: variable.phoneNumber["@id"],
          name: variable.name,
          type: "phone_number",
        };
      });

      if (this.form.domain) {
        this.loadPhoneNumbers();
      }

      this.form.excerpt = convertAssetsPathToAbsoluteLinks(this.page?.excerpt);
      this.form.preHeader = convertAssetsPathToAbsoluteLinks(this.page?.preHeader);
      this.$set(this.form, "showPreHeader", this.page?.showPreHeader || false);

      this.form.additionalSections = this.mapAdditionalSections(this.page?.additionalSections, {
        resetIds: isDuplicateForm,
      });
      this.additionalSectionsVisible = (this.form.additionalSections?.length || 0) > 0;
      this.updateMainContent(convertAssetsPathToAbsoluteLinks(this.page?.mainContent));
      // Alternative content
      this.alternativeContentVisible = !!this.page?.sharedAlternativeContent || !!this.page?.loneAlternativeContent;

      if (this.page?.loneAlternativeContent) {
        this.updateAlternativeContent(convertAssetsPathToAbsoluteLinks(this.page?.loneAlternativeContent?.content));
        this.alternativeContentConditions = this.page?.loneAlternativeContent?.conditionals || {
          ...this.alternativeContentConditions,
        };

        this.form.loneAlternativeContent = this.page?.loneAlternativeContent || {};
        this.form.loneAlternativeContent.headline = this.page?.loneAlternativeContent?.headline || "";
        this.form.sharedAlternativeContent = this.pageAlternativeContent["@id"];
        this.createPageAlternativeContent = true;
      } else if (this.page?.sharedAlternativeContent) {
        this.form.sharedAlternativeContent = this.page?.sharedAlternativeContent["@id"];
        this.form.loneAlternativeContent = {};
        this.form.loneAlternativeContent.headline = "";
      }

      this.dynamicContentVisible = !!this.page?.calendar || !!this.page?.calendarContent;
      this.form.calendar = this.page?.calendar?.["@id"];
      this.form.calendarContent = this.page?.calendarContent || {};
      this.updateDynamicContent(convertAssetsPathToAbsoluteLinks(this.page?.calendarContent?.content));

      // Set default to true for create operations, otherwise use existing value or false
      this.form.serverSideContentSplit = this.page?.serverSideContentSplit ?? !this.isEdit;

      this.form.template = this.page?.template === "main" ? "blogs_primary" : this.page?.template;
      this.form.alternativeTemplate = this.page?.alternativeTemplate || null;
      this.hasTheme = !!this.form.template;
      this.hasAlternativeTheme = !!this.form.alternativeTemplate;
      // Default page status - 'draft'. When duplicating, always set page status to 'draft'
      this.form.status = isDuplicateForm ? 1 : this.page?.status || 1;
      // Initial (default) page type lander;
      this.form.type = this.page?.type || 1;
      this.form.passQueryParameters = this.page
        ? (this.page.passQueryParameters ?? false)
        : this.form.type === PAGE_TYPE.LANDER;
      if (this.page && !this.isEdit) {
        this.form.slug = this.page?.slug + "-copy";
      }

      if (!this.isEdit) {
        delete this.form["@context"];
        delete this.form["@id"];
        delete this.form["@type"];
      }

      this.latestPageSlug = this.page?.slug || "";

      this.imagesBlockVisible = !!this.page?.featuredImage || !!this.page?.thumbnailImage;
      this.codeBlockVisible = !!this.page?.headCustomCode || !!this.page?.bodyCustomCode;

      if (!this.ability.can("view", "page type")) {
        this.form.type = 1;
        this.form.category = null;
      }

      this.form.headCustomCode = this.page?.headCustomCode || "";
      this.form.bodyCustomCode = this.page?.bodyCustomCode || "";

      this.formOriginalState = JSON.stringify(this.form);

      this.loadLabels();
      if (this.ability.can("view", "ringba user tag")) {
        this.loadRingbaTags();
      }

      if (this.page?.id) {
        this.loadStatusHistory();
      }

      // BG images
      this.pageBackgroundType = this.form.customBackgroundImage ? "image" : "color";
      this.form.customBackgroundHeight = this.form.customBackgroundHeight?.toString() || "100";
      this.form.customBackgroundImage = this.form.customBackgroundImage?.["@id"] || null;
      this.form.customBackgroundColor = this.form.customBackgroundColor || null;
      this.form.customBackgroundProperty = this.form.customBackgroundProperty || null;
      this.form.customBackgroundStartingPoint = this.form.customBackgroundStartingPoint || "bottom";

      this.form.liveChatHeader = this.page?.liveChatHeader || false;
      this.form.liveChatHeaderTemplate = this.page?.liveChatHeaderTemplate || null;
      this.form.liveChatHeaderImage = this.page?.liveChatHeaderImage?.["@id"] || null;
      this.form.liveChatHeaderName = this.page?.liveChatHeaderName || null;
    },
    mounted() {
      if (this.$refs.additionalLabelAutoComplete?.$el) {
        this.$refs.additionalLabelAutoComplete.$el
          .querySelector(".v-select__selections")
          .setAttribute("data-placeholder", this.$t("resources.pages.label_additional_placeholder"));
      }
    },
    methods: {
      mapAdditionalSections(sections, { resetIds = false } = {}) {
        return (sections || [])
          .slice()
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map(section => ({
            ...section,
            // A duplicated page must create its own sections, never reuse the original's IRI.
            "@id": resetIds ? undefined : section["@id"],
            content: convertAssetsPathToAbsoluteLinks(section.content),
            enabled: section.enabled ?? true,
            rtbLocked: section.rtbLocked ?? false,
            _key: section["@id"] || `existing-${uuidv4()}`,
            _titleAuto: !section.title || section.title === SECTION_DEFAULT_TITLE,
          }));
      },
      hasBackgroundChange(val) {
        if (!val) {
          this.form.customBackgroundColor = null;
          this.form.customBackgroundImage = null;
          this.bgColorPickerKey++;
        }
      },
      backgroundTypeChange(backgroundType) {
        if (backgroundType === "color") {
          this.form.customBackgroundImage = null;
          this.form.customBackgroundProperty = null;
          this.bgColorPickerKey++;
        } else {
          this.form.customBackgroundColor = null;
          this.form.customBackgroundProperty = "fixed";
        }
      },
      updateLiveChatHeaderImage(newValue) {
        if (newValue === "") {
          this.form.liveChatHeaderImage = null;
        } else {
          this.form.liveChatHeaderImage = newValue["@id"];
        }
      },
      updateBackgroundImage(newValue) {
        if (newValue === "") {
          this.form.customBackgroundImage = null;
        } else {
          this.form.customBackgroundImage = newValue["@id"];
        }
      },
      async saveRingbaTag() {
        if (!this.$refs.ringbaTagForm.validate()) {
          return;
        }
        try {
          const response = await this.$admin.dataProvider.create("ringba_tags", {
            data: {
              name: this.newRingbaTag.name,
            },
          });
          this.newRingbaTag = {};
          this.$admin.toast.success(this.$t("resources.pages.ringba_tag_create_success"));
          this.ringbaTags.push(response.data);
          this.ringbaTags.sort((a, b) => a.name.localeCompare(b.name));
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.isRingbaTagCreateActive = false;
        }
      },
      async loadRingbaTags() {
        this.ringbaTagListLoading = true;
        try {
          const response = await this.$admin.dataProvider.getList("ringba_tags", {
            disablePagination: true,
            sort: [{ by: "name", desc: false }],
          });
          this.ringbaTags = response.data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.ringbaTagListLoading = false;
        }
      },
      deleteRingbaTag(item) {
        this.displayDeleteRingbaTagModal = true;
        this.ringbaTagToDelete = item;
      },
      async confirmDeleteRingbaTag() {
        try {
          await this.$admin.dataProvider.customHttpCall("DELETE", `ringba_tags/${this.ringbaTagToDelete.id}`);
          this.$admin.toast.success(this.$t("resources.pages.ringba_tag_deleted_successfully"));
        } catch (error) {
          this.$admin.toast.error(this.$t("resources.pages.ringba_tag_delete_error"));
        } finally {
          this.ringbaTags = this.ringbaTags.filter(item => item["@id"] !== this.ringbaTagToDelete["@id"]);
          if (this.form.ringbaTag && this.form.ringbaTag === this.ringbaTagToDelete["@id"]) {
            this.form.ringbaTag = null;
          }
        }
        this.displayDeleteRingbaTagModal = false;
        this.ringbaTagToDelete = {};
      },
      cancelDeleteRingbaTag() {
        this.ringbaTagToDelete = {};
        this.displayDeleteRingbaTagModal = false;
      },
      getSelectedSharedAlternativeContent() {
        const item = this.$refs.alternativeContentAutocomplete?.items?.find(
          item => item["@id"] === this.form.sharedAlternativeContent,
        );

        return item;
      },
      openAbTest(e) {
        if (e.ctrlKey || e.metaKey) {
          window.open(
            this.$router.resolve({ name: "ab_tests_edit", params: { id: this.form.displayAbTest?.id } }).href,
            "_blank",
          );

          return;
        }
        if (this.needModal) {
          this.$emit("changeRedirectRouteName", "ab_tests_edit");
          this.$emit("changeRedirectRouteParams", { id: this.form.displayAbTest?.id });
          this.$emit("openModal", true);
        } else {
          this.$router.push({
            name: "ab_tests_edit",
            params: { id: this.form.displayAbTest?.id },
          });
        }
      },
      openGlobalAlternativeContent() {
        if (this.needModal) {
          this.$emit("changeRedirectRouteName", "alternative_contents_edit");
          this.$emit("changeRedirectRouteParams", { id: this.getSelectedSharedAlternativeContent()?.id });
          this.$emit("openModal", true);
        } else {
          this.$router.push({
            name: "alternative_contents_edit",
            params: { id: this.getSelectedSharedAlternativeContent()?.id },
          });
        }
      },
      sharedContentHasUrl() {
        const item = this.getSelectedSharedAlternativeContent();

        return !item?.urlPlaceholder;
      },
      alternativeContentEmpty() {
        const filteredItems = this.$refs.alternativeContentAutocomplete?.items?.filter(
          item => item["@id"] !== this.pageAlternativeContent["@id"],
        );
        if (
          (filteredItems?.length === 0 && !this.$refs.alternativeContentAutocomplete?.search) ||
          (this.pageAlternativeContent.name.includes(this.$refs.alternativeContentAutocomplete?.search) &&
            this.form.sharedAlternativeContent !== this.pageAlternativeContent["@id"] &&
            filteredItems?.length === 0)
        ) {
          return true;
        } else return false;
      },
      onCreateAlternativeContentChange(item) {
        if (item === this.pageAlternativeContent["@id"] && !this.createPageAlternativeContent) {
          this.createPageAlternativeContent = true;
          this.form.sharedAlternativeContent = this.pageAlternativeContent["@id"];
          this.$refs.alternativeContentAutocomplete.$refs.autocomplete.blur();
          this.form.loneAlternativeContent = {};
        } else if (item !== this.pageAlternativeContent["@id"]) {
          this.createPageAlternativeContent = false;
        }
      },
      async saveLabel() {
        if (!this.$refs.labelForm.validate()) {
          return;
        }
        try {
          const response = await this.$admin.dataProvider.create("labels", {
            data: {
              name: this.newLabel.name,
              color: this.newLabel.color ?? "#FFFFFF",
            },
          });
          this.newLabel = {};
          this.$admin.toast.success(this.$t("resources.pages.label_create_success"));
          this.labels.push(response.data);
          this.labels.sort((a, b) => a.name.localeCompare(b.name));
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.isLabelCreateActive = false;
        }
      },
      async loadLabels() {
        this.labelListLoading = true;
        try {
          const response = await this.$admin.dataProvider.getList("labels", {
            disablePagination: true,
            sort: [{ by: "name", desc: false }],
          });
          this.labels = response.data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.labelListLoading = false;
        }
      },
      async loadStatusHistory() {
        this.labelStatusLoading = true;
        try {
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "GET",
            `pages/${this.page.id}/upload_statuses`,
          );
          if (data.length > 0) {
            this.statusHistory = data.map(item => this.formatHistoryData(item));
          }
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.labelStatusLoading = false;
        }
      },
      formatHistoryData(item) {
        let user = "";
        if (item?.invokedBy?.firstName) {
          user = item.invokedBy.firstName;
        }

        if (item?.invokedBy?.lastName) {
          user += " " + item.invokedBy.lastName;
        }

        if (user.trim() === "") {
          user = PAGE_HISTORY_STATUS.UNKNOWN;
        }

        return {
          ...item,
          user,
          simplifiedStatus: item?.simplifiedStatus ?? "",
          actions: item.actions?.map(action => action.split("_").join(" ")).join(", ") || "",
          createdAt: getFullDateAndTime(item.createdAt),
        };
      },
      deleteLabel(item) {
        this.displayDeleteLabelModal = true;
        this.labelToDelete = item;
      },
      async confirmDeleteLabel() {
        try {
          await this.$admin.dataProvider.customHttpCall("DELETE", `labels/${this.labelToDelete.id}`);
          this.$admin.toast.success(this.$t("resources.pages.label_deleted_successfully"));
        } catch (error) {
          this.$admin.toast.error(this.$t("resources.pages.label_delete_error"));
        } finally {
          this.labels = this.labels.filter(item => item["@id"] !== this.labelToDelete["@id"]);
          if (this.form.label && this.form.label === this.labelToDelete["@id"]) {
            this.form.label = null;
          }
          this.form.additionalLabels = this.form.additionalLabels.filter(
            item => item["@id"] !== this.labelToDelete["@id"],
          );
        }
        this.displayDeleteLabelModal = false;
        this.labelToDelete = {};
      },
      cancelDeleteLabel() {
        this.labelToDelete = {};
        this.displayDeleteLabelModal = false;
      },
      confirmAlternativeContentDelete() {
        this.alternativeContentVisible = false;
        this.displayDeleteAlternativeContentModal = false;
        this.createPageAlternativeContent = false;
        this.form.sharedAlternativeContent = null;
        this.form.loneAlternativeContent = null;
        // eslint-disable-next-line vue/no-mutating-props
        this.page.sharedAlternativeContent = null;
        this.alternativeContent = "";
        this.alternativeContentConditions = {
          checkUniqueClickId: false,
          enableUTMParameters: false,
          utmSource: "",
          utmMedium: "",
          utmCampaign: "",
          utmTerm: "",
          utmContent: "",
          selectedStates: [],
          selectedCities: [],
          selectedCounties: [],
          selectedCountries: [],
          selectedDevices: [],
        };
      },
      cancelAlternativeContentDelete() {
        this.displayDeleteAlternativeContentModal = false;
      },
      confirmDynamicContentDelete() {
        this.form.calendar = null;
        if (this.$refs.form?.formState?.item) {
          this.$refs.form.formState.item.calendar = null;
        }
        this.form.calendarContent = {};
        this.dynamicContent = "";
        this.dynamicContentVisible = false;
        this.displayDeleteDynamicContentModal = false;
      },
      cancelDynamicContentDelete() {
        this.displayDeleteDynamicContentModal = false;
      },
      confirmDeleteAllSections() {
        this.form.additionalSections = [];
        this.additionalSectionsVisible = false;
        this.displayDeleteAllSectionsModal = false;
        this.recountPhoneNumbers();
      },
      cancelDeleteAllSections() {
        this.displayDeleteAllSectionsModal = false;
      },
      enableUTMParametersChange(val) {
        if (!val) {
          this.alternativeContentConditions.utmSource = "";
          this.alternativeContentConditions.utmMedium = "";
          this.alternativeContentConditions.utmCampaign = "";
          this.alternativeContentConditions.utmTerm = "";
          this.alternativeContentConditions.utmContent = "";
          this.alternativeContentConditions.checkUniqueClickId = false;
        }
      },
      finishedSaving() {
        this.$emit("finishedSaving");
      },
      handleAfterSave(data) {
        if (this.$refs?.form?.formState?.errors) {
          this.formErrors = this.$refs.form.formState.errors;
          if (this.formErrors?.serverSideContentSplit?.[0]) {
            this.$admin.toast.error(this.formErrors.serverSideContentSplit[0]);
          }
        }
        if (data) {
          this.pageVersions = data.versions;
          if (this.pageVersions.length) {
            this.setVersionItems(this.pageVersions, this.pageVersions[0]);
            this.setLastSavedLabel(this.pageVersions[0]);
          }
          this.latestPageSlug = data.slug;
          this.$emit("slugUpdated", data.slug);

          if (this.changedStatus) {
            this.changedStatus = null;
            this.form.status = data.status;
            this.$emit("statusChanged", data.status);
          }
        }
        this.afterSave();
      },
      setLastSavedLabel(pageVersion) {
        this.lastSavedPageVersionLabel = pageVersion?.updatedBy
          ? this.$t("resources.pages.current_version_label", {
              date: getFullDateAndTime(pageVersion.date),
              name: pageVersion.updatedBy,
            })
          : getFullDateAndTime(pageVersion.date);
      },
      /**
       * Set items for versions select in the modal.
       * Current version is required so that we can add "in use" label and disable the selection.
       * @param {Array} versions
       * @param {Object} currentVersion
       */
      setVersionItems(versions, currentVersion) {
        this.versionsOptions = versions.map(versionItem => {
          return {
            text: versionItem?.updatedBy
              ? this.$t("resources.pages.saved_by_full", {
                  date: getFullDateAndTime(versionItem.date),
                  name: versionItem?.updatedBy,
                })
              : getFullDateAndTime(versionItem.date),
            value: versionItem.version,
            disabled: versionItem.version === currentVersion.version,
          };
        });
      },
      createResourceHandler(resourceName) {
        const createResourceRoute = this.$router.resolve({
          name: `${resourceName}_create`,
        });

        window.open(createResourceRoute.href, "_blank");
      },
      // Manual animation is needed because tinyMCE container is dynamic height
      animatePreHeader() {
        let el = this.$refs.preHeaderTextField.$el;
        let height = el.scrollHeight;
        if (this.form.showPreHeader) {
          el.style.height = height + "px";
          var transitionend = function () {
            el.removeEventListener("transitionend", transitionend);
            el.style.height = "auto";
          };
          el.addEventListener("transitionend", transitionend);
        } else {
          let transition = el.style.transition;
          el.style.transition = "";
          requestAnimationFrame(function () {
            el.style.height = height + "px";
            el.style.transition = transition;

            requestAnimationFrame(function () {
              el.style.height = 0 + "px";
            });
          });
        }
      },
      prepareFormBeforeSave(data) {
        const lazyLoadEnabled = this.form.lazyLoad;

        if (this.alternativeContentVisible) {
          data.alternativeContent = {};

          if (
            this.form.sharedAlternativeContent &&
            this.form.sharedAlternativeContent !== this.pageAlternativeContent["@id"]
          ) {
            data.loneAlternativeContent = null;
            data.sharedAlternativeContent = this.form.sharedAlternativeContent;
          } else {
            if (this.alternativeContent) {
              const htmlElementFromContent = convertAssetsPathToRelativeLinks(this.alternativeContent);

              cleanupTinymceContent(htmlElementFromContent);

              if (lazyLoadEnabled) {
                setLazyLoad(htmlElementFromContent, 2);
              } else {
                removeLazyLoad(htmlElementFromContent);
              }

              serializeDynamicComponents(htmlElementFromContent);
              data.loneAlternativeContent.content = htmlElementFromContent.body.innerHTML;
            } else {
              data.loneAlternativeContent.content = "";
            }

            data.sharedAlternativeContent = null;
            data.sharedAlternativeContentUrl = null;
            data.loneAlternativeContent.conditionals = this.alternativeContentConditions;
            data.loneAlternativeContent.headline = this.form.loneAlternativeContent?.headline || "";
          }
        } else {
          data.loneAlternativeContent = null;
          data.sharedAlternativeContent = null;
          data.sharedAlternativeContentUrl = null;
        }

        if (this.dynamicContentVisible) {
          data.calendarContent = {};

          if (this.dynamicContent) {
            const htmlElementFromContent = convertAssetsPathToRelativeLinks(this.dynamicContent);

            cleanupTinymceContent(htmlElementFromContent);

            if (lazyLoadEnabled) {
              setLazyLoad(htmlElementFromContent, 2);
            } else {
              removeLazyLoad(htmlElementFromContent);
            }

            serializeDynamicComponents(htmlElementFromContent);
            data.calendarContent.content = htmlElementFromContent.body.innerHTML;
          } else {
            data.calendarContent.content = "";
          }

          data.calendarContent.headline = this.form.calendarContent?.headline || "";
        } else {
          data.calendarContent = null;
        }

        if (data.mainContent) {
          const htmlElementFromContent = convertAssetsPathToRelativeLinks(data.mainContent);

          cleanupTinymceContent(htmlElementFromContent);

          if (lazyLoadEnabled) {
            setLazyLoad(htmlElementFromContent, 2);
          } else {
            removeLazyLoad(htmlElementFromContent);
          }

          data.mainContent = htmlElementFromContent.body.innerHTML;
        }

        if (data.preHeader) {
          const htmlElementFromContent = convertAssetsPathToRelativeLinks(data.preHeader);

          cleanupTinymceContent(htmlElementFromContent);
          serializeDynamicComponents(htmlElementFromContent);

          data.preHeader = htmlElementFromContent.body.innerHTML;
        }

        if (data.excerpt) {
          const htmlElementFromContent = convertAssetsPathToRelativeLinks(data.excerpt);

          cleanupTinymceContent(htmlElementFromContent);

          data.excerpt = htmlElementFromContent.body.innerHTML;
        }

        if (data.additionalLabels) {
          data.additionalLabels = data.additionalLabels.map(label => label["@id"]);
        }

        if (Array.isArray(data.additionalSections)) {
          data.additionalSections = data.additionalSections
            .map((section, index) => {
              const htmlElementFromContent = convertAssetsPathToRelativeLinks(section.content || "");

              cleanupTinymceContent(htmlElementFromContent);

              if (lazyLoadEnabled) {
                setLazyLoad(htmlElementFromContent, 2);
              } else {
                removeLazyLoad(htmlElementFromContent);
              }

              serializeDynamicComponents(htmlElementFromContent);

              const payload = {
                title: section.title || SECTION_DEFAULT_TITLE,
                content: htmlElementFromContent.body.innerHTML,
                position: index,
                enabled: section.enabled,
                rtbLocked: section.rtbLocked,
              };

              if (section["@id"]) {
                payload["@id"] = section["@id"];
              }

              return payload;
            })
            // Backend requires non-blank content (PageAdditionalSection::$content); drop empty sections.
            .filter(section => section.content.trim() !== "");
        }

        /**
         * Right before we save a page, we have to serialize the comments widget content for it to become
         * a Vue template, which can then be rendered in twig templates
         */
        const content = parseToHtml(data.mainContent);
        serializeDynamicComponents(content);

        data.mainContent = content.body.innerHTML;

        /**
         * If the default sidebar of the domain is the same as the currently set sidebar,
         * We don't send it in the request.
         * The reason being that if we send the default sidebar of the domain in the Page Edit request,
         * the page will be saved in the database, and it will have sidebar ID set to the CURRENT default sidebar ID.
         * Now let's say we change the default sidebar to some other sidebar, the sidebar ID of the edited page
         * will remain as the OLD default domain sidebar ID, which would be wrong.
         */
        if (this.defaultSidebarData?.["@id"] === data.sidebar) {
          data.sidebar = null;
        }

        // Intermediate status change to prevent status change display if save fails
        if (this.changedStatus) {
          data.status = this.changedStatus;
        }

        // Convert to int
        data.customBackgroundHeight = parseInt(data.customBackgroundHeight);
        if (!data.customBackground) {
          data.customBackgroundColor = null;
          data.customBackgroundImage = null;
          data.customBackgroundProperty = null;
          data.customBackgroundHeight = null;
          data.customBackgroundStartingPoint = null;
        }

        return data;
      },
      useDomainSentrySetting() {
        this.form.sentryEnabled = null;
      },
      async domainChanged() {
        /**
         * entities - modals/sidebars
         * Only domain-specific entities can be selected, that's why we have to clear form entities if domain changes
         * Also, clear cached autocomplete search items, because some of them might belong to another domain. If not cleared,
         * user would be able select an entity which belongs to another domain
         */
        this.form.modal = null;
        this.form.label = null;
        this.form.sidebar = null;
        this.form.header = null;
        this.form.footer = null;
        this.form.alternativeHeader = null;
        this.form.alternativeFooter = null;
        this.form.alternativeHeaderEnabled = false;
        this.form.alternativeFooterEnabled = false;
        this.hasAlternativeHeader = false;
        this.hasAlternativeFooter = false;
        this.form.favoritesBlock = null;
        this.form.category = null;
        this.form.variables = [];
        this.isPhoneNumberCreateActive = false;
        this.phoneNumbers = [];
        this.loadPhoneNumbers();
      },
      pageTypeChanged(val) {
        if (val !== PAGE_TYPE.ARTICLE) {
          this.form.useHomepageTheme = false;
        }

        // If page is a lander type
        if (val === PAGE_TYPE.LANDER) {
          this.form.category = null;
          // Remove sidebar
          this.form.hasSidebar = false;
          this.form.sidebar = null;
          // Remove favorites block
          this.form.showFavoritesBlock = false;
          this.form.favoritesBlock = null;
          this.form.thumbnailImage = null;
        }

        if (!this.isEdit) {
          this.form.passQueryParameters = val === PAGE_TYPE.LANDER;
        }

        // If page is a Form page
        if (val === PAGE_TYPE.AUDIO_FORM) {
          this.form.hasHeader = false;
          this.form.header = null;
        }

        // If page is not an article
        if (val !== PAGE_TYPE.ARTICLE) {
          this.form.category = null;
        }
      },
      updateFeaturedImage(newValue) {
        if (newValue === "") {
          this.form.featuredImage = null;
        } else {
          this.form.featuredImage = newValue["@id"];
        }
      },
      updateThumbnailImage(newValue) {
        if (newValue === "") {
          this.form.thumbnailImage = null;
        } else {
          this.form.thumbnailImage = newValue["@id"];
        }
      },
      updatePreHeader(newValue) {
        this.form.preHeader = newValue;
      },
      updateExcerptText(newValue) {
        this.form.excerpt = newValue;
      },

      processElements(elements, dataExtractor, counts) {
        elements?.forEach(element => {
          const data = dataExtractor(element);

          // Define regex for finding complete ringbaPhoneNumber objects with all their data
          const ringbaRegex = /&quot;ringbaPhoneNumber&quot;\s*:\s*{(?:[^{}]|&quot;[^&]*&quot;)*}/g;

          // Try to find ringbaPhoneNumber data using regex
          const ringbaMatches = data?.match(ringbaRegex) ?? [];

          ringbaMatches.forEach(match => {
            const decodedMatch = match
              .replace(/&quot;/g, '"')
              .replace(/&amp;/g, "&")
              .replace(/&#34;/g, '"');

            const phoneData = JSON.parse(`{${decodedMatch}}`);
            const uuid = phoneData.ringbaPhoneNumber.value;
            const text = phoneData.ringbaPhoneNumber.text;

            counts[uuid] = text;
          });
        });
      },

      countUUIDs(htmlElement, counts) {
        this.processElements(
          htmlElement.querySelectorAll("div[data-live-chat-data]"),
          element => window.atob(element.dataset.liveChatData),
          counts,
        );

        this.processElements(
          htmlElement.querySelectorAll("div[data-live-chat-mp4-data]"),
          element => window.atob(element.dataset.liveChatMp4Data),
          counts,
        );

        this.processElements(
          htmlElement.querySelectorAll("div[data-ringba-phone-number]"),
          element => element.outerHTML,
          counts,
        );
      },

      // Phone-number usage is counted across every editable surface (main content + all additional sections),
      // so a number used only inside a section still counts as "used on page".
      recountPhoneNumbers() {
        const counts = {};
        const contents = [this.form.mainContent, ...(this.form.additionalSections || []).map(section => section.content)];

        contents.forEach(content => {
          if (content) {
            this.countUUIDs(parseToHtml(content), counts);
          }
        });

        this.ringbaPhoneNumberCounts = counts;
      },

      updateMainContent(newValue) {
        let htmlElementFromContent = parseToHtml(newValue);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        this.form.mainContent = htmlElementFromContent.body.innerHTML;
        this.recountPhoneNumbers();
      },
      openReplaceContentModal() {
        this.replaceSnapshot = null;
        this.displayReplaceContentModal = true;
      },
      closeReplaceContentModal() {
        this.displayReplaceContentModal = false;
        this.replaceSnapshot = null;
      },
      replaceAllContent({ search, replace }) {
        if (!search) return;

        const sections = this.form.additionalSections || [];

        const sectionsSnapshot = {};
        sections.forEach(section => {
          sectionsSnapshot[section._key] = section.content;
        });

        this.replaceSnapshot = {
          mainContent: this.form.mainContent || "",
          sections: sectionsSnapshot,
        };

        this.updateMainContent(replaceInContent(this.form.mainContent, search, replace));

        sections.forEach(section => {
          section.content = replaceInContent(section.content, search, replace);
        });
        // Programmatic content change doesn't go through the editor, so recount explicitly.
        this.recountPhoneNumbers();

        this.versionKey = uuidv4();
        this.$refs.additionalSectionsPanel?.refreshEditors();
      },
      undoReplaceContent() {
        if (!this.replaceSnapshot) return;

        this.updateMainContent(this.replaceSnapshot.mainContent);
        (this.form.additionalSections || []).forEach(section => {
          const original = this.replaceSnapshot.sections[section._key];

          if (original !== undefined) {
            section.content = original;
          }
        });
        // Programmatic content change doesn't go through the editor, so recount explicitly.
        this.recountPhoneNumbers();

        this.versionKey = uuidv4();
        this.$refs.additionalSectionsPanel?.refreshEditors();
        this.replaceSnapshot = null;
      },
      updateAlternativeContent(newValue) {
        let htmlElementFromContent = parseToHtml(newValue);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        this.alternativeContent = htmlElementFromContent.body.innerHTML;
      },

      updateDynamicContent(newValue) {
        let htmlElementFromContent = parseToHtml(newValue);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        this.dynamicContent = htmlElementFromContent.body.innerHTML;
      },
      showHeaderChange(val) {
        if (!val) {
          this.form.header = null;
        }
      },
      showLivechatHeaderChange(val) {
        if (!val) {
          this.form.liveChatHeaderTemplate = null;
          this.form.liveChatHeaderImage = null;
          this.form.liveChatHeaderName = null;
        }
      },
      showFooterChange(val) {
        if (!val) {
          this.form.footer = null;
        }
      },
      showAlternativeHeaderChange(val) {
        this.hasAlternativeHeader = val;
        this.form.alternativeHeaderEnabled = val;
        this.form.alternativeHeader = null;
      },
      showAlternativeFooterChange(val) {
        this.hasAlternativeFooter = val;
        this.form.alternativeFooterEnabled = val;
        if (!val) {
          this.form.alternativeFooter = null;
        }
      },
      showThemeChange(val) {
        this.hasTheme = val;
        if (!val) {
          this.form.template = null;
        }
      },
      showAlternativeThemeChange(val) {
        this.hasAlternativeTheme = val;
        if (!val) {
          this.form.alternativeTemplate = null;
        }
      },
      showPopupChange(val) {
        if (!val) {
          this.form.modal = null;
        }
      },
      showSidebarChange(val) {
        if (!val) {
          this.form.sidebar = null;
        }
      },
      showFavoritesBlockChange(val) {
        if (!val) {
          this.form.favoritesBlock = null;
        }
      },
      async previewPageVersion() {
        try {
          await this.preview(this.latestPageSlug, this.page.domain.replace("/api/domains/", ""), this.selectedVersion);
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        }
      },
      handleDisplayVersionHistoryModal() {
        this.displayVersionHistoryModal = true;
      },
      handleVersionChange(version) {
        this.selectedVersion = version;
      },
      async confirmVersionChange() {
        await this.changePageVersion(this.selectedVersion);
        this.selectedVersion = null;
        this.displayVersionHistoryModal = false;
      },
      cancelVersionChange() {
        this.displayVersionHistoryModal = false;
        this.selectedVersion = null;
      },
      async changePageVersion(version) {
        try {
          this.changePageVersionLoading = true;
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "GET",
            `pages/${this.page.id}/version/${version}`,
          );

          for (const [dataKey, dataValue] of Object.entries(data)) {
            this.form[dataKey] = dataValue;
          }

          this.updatePreHeader(convertAssetsPathToAbsoluteLinks(this.form.preHeader));
          // animatePreHeader leaves an inline height behind, which would keep the editor
          // collapsed when a restored version switches the pre-headline back on.
          const preHeaderEl = this.$refs.preHeaderTextField?.$el;

          if (preHeaderEl) {
            preHeaderEl.style.height = "";
          }

          this.updateExcerptText(convertAssetsPathToAbsoluteLinks(this.form.excerpt));
          this.updateMainContent(convertAssetsPathToAbsoluteLinks(this.form.mainContent));

          this.form.additionalSections = this.mapAdditionalSections(data?.additionalSections);
          this.additionalSectionsVisible = (this.form.additionalSections?.length || 0) > 0;
          this.$nextTick(() => this.$refs.additionalSectionsPanel?.refreshEditors());

          this.versionKey = version;

          this.form.thumbnailImage = data?.thumbnailImage?.["@id"];
          this.form.featuredImage = data?.featuredImage?.["@id"];

          this.alternativeContentVisible = !!data?.sharedAlternativeContent || !!data?.loneAlternativeContent;

          if (data?.loneAlternativeContent) {
            this.updateAlternativeContent(convertAssetsPathToAbsoluteLinks(data.loneAlternativeContent?.content));
            this.alternativeContentConditions = data.loneAlternativeContent?.conditionals;

            this.form.loneAlternativeContent = data.loneAlternativeContent;
            this.form.loneAlternativeContent.headline = data.loneAlternativeContent?.headline;
            this.form.sharedAlternativeContent = this.pageAlternativeContent["@id"];
            this.createPageAlternativeContent = true;
          }

          this.dynamicContentVisible = !!data?.calendar || !!data?.calendarContent;
          this.updateDynamicContent(convertAssetsPathToAbsoluteLinks(data?.calendarContent?.content));

          const newCurrentVersion = this.pageVersions.find(versionItem => versionItem.version === version);
          this.setVersionItems(this.pageVersions, newCurrentVersion);

          this.hasTheme = !!this.form.template;
          this.hasAlternativeTheme = !!this.form.alternativeTemplate;
          this.showRingbaParameters = data?.ringbaParameters?.length || false;

          this.$nextTick(() => {
            this.form.ringbaParameters = data?.ringbaParameters || [];
          });
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.changePageVersionLoading = false;
        }
      },

      submit(toastText, linkText) {
        this.successRedirectLink = {
          toastText,
          linkText,
          linkPath: this.fullUrl,
        };
        // Need this timeout for toast text to be displayed
        setTimeout(() => {
          this.setSubmitAttempted();

          if (
            this.imageUploadQueue?.length > 0 ||
            this.excerptImageUploadQueue?.length > 0 ||
            this.alternativeContentImageUploadQueue?.length > 0 ||
            this.dynamicContentImageUploadQueue?.length > 0 ||
            this.preHeaderImageUploadQueue?.length > 0
          ) {
            this.$admin.toast.error(this.$t("resources.pages.images_still_uploading"));
            this.finishedSaving();

            return;
          }

          const invalidTelHrefs = [
            ...findInvalidTelHrefs(this.form?.mainContent),
            ...findInvalidTelHrefs(this.form?.preHeader),
            ...findInvalidTelHrefs(this.form?.excerpt),
            ...findInvalidTelHrefs(this.alternativeContent),
            ...findInvalidTelHrefs(this.dynamicContent),
          ];

          if (invalidTelHrefs.length > 0) {
            showTelConfirmModal(
              this.$t("shortcodes.live_chat.invalid_tel_href_confirm", { count: invalidTelHrefs.length }),
              invalidTelHrefs.join(", "),
            ).then(confirmed => {
              if (confirmed) {
                this.$refs.form.onSubmit();
              } else {
                this.finishedSaving();
              }
            });
            return;
          }

          this.$refs.form.onSubmit();
        }, 0);
      },
      handleRingbaSwitch(val) {
        if (!val) {
          this.form.ringbaParameters = [];
        } else {
          this.form.ringbaParameters = [
            {
              tag: "",
              number: "",
            },
          ];
        }
      },
      togglePhoneNumberBlock(id) {
        if (this.phoneNumberBlockVisible.includes(id)) {
          this.phoneNumberBlockVisible = this.phoneNumberBlockVisible.filter(item => item !== id);
        } else {
          this.phoneNumberBlockVisible.push(id);
        }
      },
      handlePhoneNumberChange(phoneNumber) {
        this.displayActionPhoneNumberModal = "save";
        this.replacePhoneNumberVariable = phoneNumber;
      },

      cancelPhoneNumberSave() {
        this.cancelPhoneNumberAction();
      },

      async confirmPhoneNumberSaveNotice() {
        // Find the existing variable with the same name
        const existingVariableIndex = this.form.variables.findIndex(
          variable => variable.name === this.replacePhoneNumberVariable.name,
        );

        this.phoneNumberActionData = this.phoneNumberVariables.find(
          variable => variable.name === this.replacePhoneNumberVariable.name,
        );

        if (existingVariableIndex !== -1) {
          this.form.variables.splice(existingVariableIndex, 1, {
            name: this.replacePhoneNumberVariable.name,
            phoneNumber: this.replacePhoneNumberVariable.phoneNumberData["@id"],
            type: VARIABLE_TYPES.PHONE_NUMBER,
          });
        } else if (this.phoneNumberActionData.type === VARIABLE_TYPES.PHONE_NUMBER_OLD) {
          this.form.variables.push({
            name: this.replacePhoneNumberVariable.name,
            phoneNumber: this.replacePhoneNumberVariable.phoneNumberData["@id"],
            type: VARIABLE_TYPES.PHONE_NUMBER,
          });
        }

        let htmlElementFromContent = this.updatePhoneNumberInContent(this.form.mainContent);
        this.updateMainContent(htmlElementFromContent.body.innerHTML);

        this.phoneNumberActionData = null;
        this.phoneNumberBlockVisible = [];
        this.replacePhoneNumberVariable = null;
        this.displayActionPhoneNumberModal = null;
        this.versionKey = uuidv4();
      },

      async loadPhoneNumbers() {
        const { data } = await this.$admin.dataProvider.customHttpCall(
          "GET",
          `phone_numbers?status=active&domain=${this.form.domain}`,
          {
            domain: this.form.domain,
          },
        );
        this.phoneNumbers = data;
        this.$nextTick(() => {
          this.checkMissingPhoneNumbers();
        });
      },

      onCampaignChange() {
        // Clear selected campaign when publisher changes
        this.selectedPublisher = null;
      },

      addPhoneNumber(data) {
        const uuid = uuidv4();
        this.form.variables.push({
          name: uuid,
          phoneNumber: data?.phoneNumberData?.["@id"],
          type: "phone_number",
        });
        this.isPhoneNumberCreateActive = false;
        this.phoneNumberBlockVisible = [];
      },

      removePhoneNumber(variable) {
        // Remove the phone number from form.variables array
        if (!!this.ringbaPhoneNumberCounts[variable.name] && this.phoneNumberVariables.length === 1) {
          this.displayActionPhoneNumberModal = "impossible_delete";
        } else if (!!this.ringbaPhoneNumberCounts[variable.name] && this.phoneNumberVariables.length > 1) {
          this.displayActionPhoneNumberModal = "need_to_replace";
        } else {
          this.displayActionPhoneNumberModal = "delete";
        }

        this.phoneNumberActionData = variable;
      },

      confirmDeletePhoneNumber() {
        this.form.variables = this.form.variables.filter(v => v.name !== this.phoneNumberActionData.name);

        this.phoneNumberBlockVisible = [];
        this.phoneNumberActionData = null;
        this.displayActionPhoneNumberModal = null;
      },

      cancelPhoneNumberAction() {
        this.displayActionPhoneNumberModal = null;
        this.phoneNumberActionData = null;
      },

      updatePhoneNumberInContent(html) {
        let htmlElementFromContent = parseToHtml(html);

        // Define element types to process
        const elementTypes = [
          {
            selector: "div[data-live-chat-data]",
            dataAttr: "liveChatData",
            attributeName: "data-live-chat-data",
            needsEncoding: true,
          },
          {
            selector: "div[data-ringba-phone-number]",
            dataAttr: "ringbaPhoneNumber",
            attributeName: "data-ringba-phone-number",
            needsEncoding: false,
          },
        ];

        // Process each element type
        elementTypes.forEach(({ selector, dataAttr, attributeName, needsEncoding }) => {
          const elements = htmlElementFromContent.querySelectorAll(selector);

          elements.forEach(element => {
            let content = needsEncoding ? window.atob(element.dataset[dataAttr]) : element.dataset[dataAttr];
            // Replace phone number data if available
            if (this.phoneNumberActionData.name && this.replacePhoneNumberVariable.name) {
              content = content.replaceAll(this.phoneNumberActionData.name, this.replacePhoneNumberVariable.name);
            }

            if (this.phoneNumberActionData.text && this.replacePhoneNumberVariable.text) {
              content = content.replaceAll(this.phoneNumberActionData.text, this.replacePhoneNumberVariable.text);
            }

            // Set the updated content
            const finalContent = needsEncoding ? window.btoa(content) : content;
            element.setAttribute(attributeName, finalContent);
          });
        });

        return htmlElementFromContent;
      },

      confirmPhoneNumberReplace() {
        let htmlElementFromContent = this.updatePhoneNumberInContent(this.form.mainContent);
        this.updateMainContent(htmlElementFromContent.body.innerHTML);

        this.replacePhoneNumberVariable = null;
        this.phoneNumberBlockVisible = [];
        this.versionKey = uuidv4();
        this.confirmDeletePhoneNumber();
      },

      handleEditPhoneNumbers() {
        this.cancelPhoneNumberAction();
        // Scroll to phone numbers block
        const phoneNumbersBlock = document.querySelector(".phone-number-card-title-wrapper");
        if (phoneNumbersBlock) {
          phoneNumbersBlock.scrollIntoView({ behavior: "smooth" });
        }
      },

      checkMissingPhoneNumbers() {
        const hasMissingPhoneNumbers = this.phoneNumberVariables.some(
          variable => variable.type === VARIABLE_TYPES.PHONE_NUMBER_OLD,
        );

        if (hasMissingPhoneNumbers) {
          this.displayActionPhoneNumberModal = "missing_phone_numbers";
        }
      },

      beforeSave() {
        if (this.isEdit) {
          this.checkMissingPhoneNumbers();
        }
      },
    },
  };
</script>
<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .page-subcard-disclaimer {
    margin-top: 8px;
    @include text-caption();
    color: get-color("subtitle");
  }
  .pre-header-input {
    .label {
      color: get-color("subtitle");
      @include text-caption-bold();
    }
    .pre-header-wrapper {
      border: 1px solid get-color("border");
      border-radius: 4px;
      padding: 11px 12px;
      color: get-color("subtitle");
      .pre-header-input {
        transition: 0.3s ease-in-out;
        &.visible {
          margin-top: 6px;
          opacity: 1;
        }
        &.hidden {
          height: 0;
          opacity: 0;
          overflow: hidden;
        }
      }
      .input-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 14px;

        .v-input--switch {
          &::v-deep .v-input--selection-controls__input {
            margin: 0;
          }
        }
      }
    }
  }

  .card-title {
    margin-bottom: 16px;
    .card-icon {
      color: get-color("icon") !important;
      margin-right: 6px;
    }
  }

  .url-wrapper {
    margin-top: 8px;
    @include text-caption();
    color: get-color("subtitle");
    display: flex;
    justify-content: space-between;
    align-items: center;

    a {
      color: get-color("body");
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        color: get-color("primary");
      }
    }
  }

  .upload-history-block {
    border-top: 1px dashed get-color("border");
    padding-top: 16px;

    &__first-line {
      @include text-body-2();
      color: get-color("subtitle");
      margin-right: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__status-chip {
      @include text-caption();
      height: 24px;
    }
    &__title {
      padding-right: 4px;
    }
    &__content {
      @include text-body-2();
      color: get-color("subtitle");
    }
    &__status {
      text-transform: capitalize;
    }
    &__label {
      @include text-caption-bold();
      color: get-color("subtitle");
    }
    &__info {
      @include text-body-2();
      color: get-color("subtitle");
      margin-right: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &__view-link {
      @include text-button-small();
      display: flex;
      align-items: center;
      color: get-color("secondary-btn");
      cursor: pointer;
      white-space: nowrap;

      &:hover {
        color: get-color("same-hover");
      }
    }
  }

  .version-description-block {
    padding-top: 16px;
    .v-text-field__details {
      display: none;
    }
  }

  .page-version-block {
    &__label {
      @include text-caption-bold();
      color: get-color("subtitle");
      margin-bottom: 6px;
    }
    &__info {
      @include text-body-2();
      color: get-color("subtitle");
      margin-right: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &__view-history {
      @include text-button-small();
      color: get-color("secondary-btn");
      cursor: pointer;
      white-space: nowrap;

      &:hover {
        color: get-color("same-hover");
      }
    }
  }

  .preview_selected_version {
    @include text-caption();
    color: get-color("subtitle");
    margin-top: 8px;
  }

  .preview-version-label {
    @include text-caption();
    color: get-color("primary");
    cursor: pointer;
    margin-top: 8px;
    i {
      color: get-color("primary");
    }
  }

  .in-use-chip {
    height: 24px;
    &::v-deep {
      .v-chip__content {
        @include text-caption();
      }
    }
  }

  .status-chip {
    @include text-caption();
    height: 24px;

    &::v-deep {
      &.v-chip.success {
        background-color: get-color("success-120") !important;
        .v-chip__content {
          color: get-color("success");
        }
      }
    }
  }

  .chip-outlined {
    background: none !important;
    border: 1px solid get-color("subtitle");
  }

  .details-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .alternative-content {
    &__title {
      @include heading-4();
      color: get-color("body");
      margin-bottom: 4px;
    }
    &__subtitle {
      @include text-caption();
      color: get-color("subtitle");
    }
    &__add-btn {
      min-width: 90px !important;
      max-width: 90px !important;

      &:hover {
        background: get-color("primary-10-hover");
      }

      ::v-deep {
        svg path {
          fill: get-color("secondary-btn") !important;
        }

        span {
          color: get-color("secondary-btn") !important;
        }
      }
    }
    &__delete-btn {
      &:hover {
        background: get-color("primary-10-hover");
      }
      &::v-deep {
        .v-icon {
          margin-right: 6px;
        }
      }
    }
    .enable-utm-parameters-switch,
    .enable-cloaking-parameter-switch {
      margin-right: 2px;
      &::v-deep {
        .v-input--selection-controls__input {
          margin-right: 6px;
        }
      }
    }

    .max-w-50 {
      max-width: 50%;
    }

    .prepend-icon-hidden {
      &::v-deep {
        .v-input__prepend-inner {
          display: none;
        }
        .v-input.v-select.v-select--is-multi.v-autocomplete--with-label .v-label,
        .v-input.v-autocomplete.v-select--is-multi.v-autocomplete--with-label .v-label {
          left: 0 !important;
        }
        .v-input.v-select.v-select--is-multi .v-label {
          left: 0 !important;
        }
      }
    }
  }

  .page-setting-label {
    @include text-body-2();
    color: get-color("subtitle");
  }
  .page-setting-switch {
    &::v-deep {
      .v-input--selection-controls__input {
        margin-right: 0;
      }
    }
  }

  .page-setting-caption {
    @include text-caption();
    color: get-color("subtitle");
  }

  .page-setting-reset {
    @include text-button-small();
    color: get-color("secondary-btn");
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      color: get-color("same-hover");
    }
  }

  .ringba-phone-input-caption {
    @include text-caption();
    color: get-color("subtitle");
  }

  .ringba-block {
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px dashed get-color("border");
  }

  .label-title {
    @include text-body-2();
  }

  .label-badge {
    // Important needed to overwrite default avatar properties
    border: 1px solid get-color("border") !important;
  }

  .label-wrapper {
    @include text-button-small();
    color: get-color("secondary-btn");
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;

    i {
      transition: none;
      color: get-color("secondary-btn") !important;
    }

    &:hover {
      cursor: pointer;
      color: get-color("same-hover");

      i {
        color: get-color("same-hover") !important;
      }
    }
  }

  #label-creator {
    border: 1px solid get-color("border");
    border-radius: 4px;
    padding: 16px;
  }

  .label-select-item {
    padding: 8px 0;
    &:hover {
      .label-select-item__close-icon {
        display: inline-flex !important;
      }
    }
    &__close-icon {
      margin-left: auto;
      margin-right: 16px;
    }
  }

  .page-elements-card-subtitle {
    @include text-body-2();
    color: get-color("subtitle");
    margin: 0;
  }

  .label-selector {
    margin-top: 16px;
    margin-bottom: 4px;
  }

  .page-alternative-content-click-parameters-title {
    @include text-body-2-bold();
    color: get-color("subtitle");
    margin-bottom: 12px;
  }

  .page-elements-panel-wrapper {
    ::v-deep .v-expansion-panel-header {
      min-height: unset;
    }
  }

  .page-element-autocomplete {
    ::v-deep {
      .v-input.v-select.v-autocomplete--with-label,
      .v-input.v-autocomplete.v-autocomplete--with-label {
        padding-top: 25px;
      }
    }
  }
  .labels-empty-subtitle {
    @include text-subtitle-2();
    color: get-color("subtitle") !important;
    margin-bottom: 0;
  }

  .label-card-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .label-add-button {
    color: get-color("secondary-btn");
    @include text-button-medium();
    display: flex;
    align-items: center;

    &:hover {
      cursor: pointer;
      color: get-color("same-hover");

      ::v-deep path {
        fill: get-color("same-hover") !important;
      }
    }
  }

  .label-cancel-button {
    border-color: get-color("icon");
  }

  .label-create-button-wrapper {
    display: flex;
    gap: 16px;
    margin-top: 16px;

    button {
      flex: 1;
    }
  }

  .label-form {
    ::v-deep {
      .v-messages__message {
        margin-top: 4px !important;
        margin-bottom: 8px;
      }
    }
  }

  .label-name-input {
    margin-bottom: 2px;
  }

  .label-primary-select {
    margin-bottom: 2px;

    ::v-deep {
      .v-input__append-inner {
        padding: 0;

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .additional-label-autocomplete {
    ::v-deep {
      .v-select__selections input::placeholder {
        color: get-color("subtitle") !important;
        opacity: 1 !important;
      }

      .v-list-item {
        @include text-body-2();
      }
    }
  }

  .additional-label-autocomplete:not(.v-autocomplete--search-added) {
    ::v-deep {
      .v-select__selections::after {
        position: absolute;
        content: attr(data-placeholder);
        color: get-color("subtitle") !important;
        opacity: 1 !important;
      }
    }
  }

  .additional-label-item-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .additional-label-item-inner-wrapper {
    display: flex;
    align-items: center;
  }

  .additional-label-chip {
    @include text-caption();
  }

  .dynamic-content {
    &__title {
      @include heading-4();
      color: get-color("body");
      margin-bottom: 4px;
    }
    &__subtitle {
      @include text-caption();
      color: get-color("subtitle");
    }
    &__add-btn {
      min-width: 90px !important;
      max-width: 90px !important;

      &:hover {
        background: get-color("primary-10-hover");
      }

      ::v-deep {
        svg path {
          fill: get-color("secondary-btn") !important;
        }

        span {
          color: get-color("secondary-btn") !important;
        }
      }
    }
    &__delete-btn {
      &:hover {
        background: get-color("primary-10-hover");
      }
      &::v-deep {
        .v-icon {
          margin-right: 6px;
        }
      }
    }
  }

  .additional-sections-block {
    &__title {
      @include heading-4();
      color: get-color("body");
      margin-bottom: 4px;
    }
    &__subtitle {
      @include text-caption();
      color: get-color("subtitle");
    }
    &__add-btn {
      &:hover {
        background: get-color("primary-10-hover");
      }

      ::v-deep {
        svg path {
          fill: get-color("secondary-btn") !important;
        }

        span {
          color: get-color("secondary-btn") !important;
        }
      }
    }
  }

  .error-wrapper {
    margin-top: 8px;
    @include text-caption();
    color: get-color("error");
  }

  .server-side-cloaking-wrapper {
    margin-top: 16px;
    margin-bottom: 16px;
    @include text-body-2();
    color: get-color("body");
    background: get-color("element-bg-200");
    padding: 12px;
    border-radius: 4px;

    & > div {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &::v-deep {
        .v-input--selection-controls__input {
          margin-right: 0;
        }
      }
    }
  }
  .create-alternative-content-item {
    border-top: 1px solid get-color("border");
    margin: 0 8px;
    padding: 0 8px;
    cursor: pointer;
  }

  .create-alternative-content-action {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-direction: row;

    width: 100%;
    justify-content: flex-start;

    & > span {
      @include text-caption();
      color: get-color("primary");
    }
  }

  .create-alternative-content-select {
    margin-top: 16px;
    margin-bottom: 16px;

    ::v-deep {
      .v-input__append-inner {
        padding: 0;

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .alternative-content-item {
    display: flex;
    align-items: center;
    justify-content: space-between;

    & > a {
      display: flex;
      align-items: center;
      gap: 4px;
      @include text-body-2();
      color: get-color("primary");
      text-decoration: none;
    }
  }

  .alternative-no-url-disclaimer {
    margin-top: 16px;
    border-radius: 4px;
    border: 1px solid get-color("accent");
    background: get-color("accent-120");
    padding: 11px 16px;
    display: flex;
    gap: 12px;
    align-items: center;
    @include text-caption();

    &-link {
      color: get-color("primary");
      text-decoration: none;
      cursor: pointer;
    }
  }

  .ab-test-label {
    @include text-caption-bold();
    color: get-color("subtitle");
    display: flex;
    gap: 6px;
    align-items: center;
    margin-bottom: 8px;
  }

  .active-chip {
    color: get-color("info");
  }

  .status-chip {
    @include text-caption();
    height: unset;
    padding: 4px 12px;
  }

  .ab-test-name {
    @include text-caption();
    color: get-color("body");
    text-decoration: underline;
    cursor: pointer;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    &:hover {
      color: get-color("primary");
    }
  }

  .inactive-chip {
    background-color: get-color("element-bg-100") !important;
    border: 1px solid get-color("border") !important;
    .v-chip__content {
      color: get-color("disabled") !important;
    }
  }

  .ringba-tag-select-item {
    padding: 8px 0;
    &:hover {
      .ringba-tag-select-item__close-icon {
        display: inline-flex !important;
      }
    }
    &__close-icon {
      margin-left: auto;
      margin-right: 16px;
    }
  }

  .ringba-tag-selector {
    margin-top: 16px;
    margin-bottom: 4px;
  }

  .ringba-tags-empty-subtitle,
  .sentry-card-subtitle {
    @include text-subtitle-2();
    color: get-color("subtitle") !important;
    margin-bottom: 0;
  }

  .ringba-tag-card-title-wrapper,
  .sentry-card-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .sentry-card-title-wrapper .page-setting-switch {
    margin-top: 0;
    padding-top: 0;
  }

  .ringba-tag-add-button {
    color: get-color("secondary-btn");
    @include text-button-medium();
    display: flex;
    align-items: center;

    &:hover {
      cursor: pointer;
      color: get-color("same-hover");

      ::v-deep path {
        fill: get-color("same-hover") !important;
      }
    }
  }

  .ringba-tag-cancel-button {
    border-color: get-color("icon");
  }

  .ringba-tag-create-button-wrapper {
    display: flex;
    gap: 16px;
    margin-top: 16px;

    button {
      flex: 1;
    }
  }

  .ringba-tag-form {
    ::v-deep {
      .v-messages__message {
        margin-top: 4px !important;
        margin-bottom: 8px;
      }
    }
  }

  .ringba-tag-name-input {
    margin-bottom: 2px;
  }

  .ringba-tag-primary-select {
    margin-bottom: 2px;

    ::v-deep {
      .v-input__append-inner {
        padding: 0;

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }
  .additional-label-item-inner-wrapper {
    display: flex;
    align-items: center;
  }

  .ringba-tag-item-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .phone-number-variable {
    &-wrapper {
      width: 100%;
      .v-expansion-panels {
        width: 100%;
      }

      .v-expansion-panel {
        background-color: get-color("hover");
        border-radius: 4px;
      }

      .v-expansion-panel {
        &.v-expansion-panel--active {
          box-shadow: none !important;
          & > .v-expansion-panel-header {
            min-height: auto;
          }
        }
        &:before {
          display: none;
        }
      }
    }
    &-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      overflow: hidden;
      @include text-caption();
    }
    &-content {
      padding-top: 12px;
      border-top: 1px dashed get-color("border");
    }
    &-action-buttons {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      flex-direction: row;
      .v-btn.v-size--default {
        padding: 0;
        min-width: auto;
        height: auto !important;
      }
      &--border {
        border-top: 1px dashed get-color("border");
        margin-top: 12px;
        padding-top: 12px;
      }
    }
    &-delete-button {
      @include text-button-small();
      display: flex;
      align-items: center;
    }
    &-save-button {
      @include text-button-small();
      display: flex;
      align-items: center;
    }
    &-empty-subtitle {
      @include text-subtitle-2();
      color: get-color("subtitle") !important;
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }
  .phone-number-card-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .phone-number-variable-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .phone-number-variable-header > span:first-child {
    display: flex;
    align-items: center;
    min-width: 0; /* Important for text-truncate to work */
  }

  .text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 8px; /* Add some space between the text and chip */
  }
</style>

<style>
  .rotate-180 {
    transform: rotate(180deg);
    transition: transform 0.3s ease;
  }

  .mdi-chevron-down {
    transition: transform 0.3s ease;

    .va-btn-toggle {
      border-radius: 4px;
      .v-btn {
        margin: 0;
        box-shadow: none !important;
        border: 1px solid get-color("secondary-btn");
        color: get-color("secondary-btn");
      }
    }
  }

  .required-text {
    margin-top: 16px;
    @include text-caption();
    color: get-color("subtitle");

    span {
      color: get-color("primary");
    }
  }
</style>
